import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import DocumentUploadField from '../../../Components/Common/DocumentUploadField';
import MultipleDocumentUpload from '../../../Components/Common/MultipleDocumentUpload';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthToken } from '../../../utils/authUtils';
import { API_ENDPOINTS } from '../../../Api/endpoints';
import { generateDocumentUrl } from '../../../utils/documentUtils';
import { ROUTES } from '../../../routes';
import apiService from '../../../services/api';
import { showLoader, dismissLoader } from '../../../LayoutContainers/AppLayout/redux/AppSlice';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Paper,
  CardContent,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  Snackbar,
  Switch,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Popover,
  Fade
} from '@mui/material';
import { 
  ArrowBack,
  NavigateNext,
  NavigateBefore,
  Person,
  Work,
  ContactMail,
  CloudUpload,
  Delete,
  ExpandMore,
  Warning as WarningIcon,
  AssignmentInd as AssignmentIndIcon,
  Flight as FlightIcon,
  Person as PersonIcon,
  Description,
  Payment as PaymentIcon,
  AttachFile as AttachFileIcon,
  School as SchoolIcon,
  Apartment as ApartmentIcon,
  Add as AddIcon,
  CheckCircle,
  CheckCircleOutline as CheckCircleOutlineIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  VpnKey,
  Save as SaveIcon,
  Save,
  Visibility,
  DataObject as DataObjectIcon,
  Info as InfoIcon,
  CheckCircleOutline,
  AutoFixHigh,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  PhotoCamera,
  Image as ImageIcon
} from '@mui/icons-material';

// Reuse employee status options from EmployeeEdit for consistency
import { EMPLOYEE_STATUS_OPTIONS } from './EmployeeEdit';

// Normalize UI document types to backend snake_case documentType values
// Dynamic service fields (UUIDs) should be passed through as-is by callers
const toBackendDocumentTypeLocal = (t) => {
  switch (t) {
    case 'passportCopy':
      return 'passport_copy';
    case 'visaPage':
      return 'visa_page';
    case 'salaryCertificate':
      return 'salary_certificate';
    case 'birthCertificate':
      return 'birth_certificate';
    case 'emiratesId':
      return 'emirates_id';
    case 'photo':
      return 'photo';
    default:
      // Fallback: convert camelCase/PascalCase to snake_case and lowercase
      return (t || 'unknown').replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
  }
};


// Gender options
const GENDERS = [
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'OTHER', label: 'Other' },
];

// Steps for the employee creation wizard
const employeeSteps = [
  'Personal Information',
  'Employment Details',
  'Contact & Access'
];

const stakeholderSteps = [
  'Personal Information',
  'Stakeholder Details',
  'Contact & Access'
];

const STAKEHOLDER_ROLES = [
  { value: 'OWNER', label: 'Owner' },
  { value: 'PARTNER', label: 'Partner' },
  { value: 'DIRECTOR', label: 'Director' },
];

function EmployeeCreate({ mode = 'employee' }) {
  const isStakeholder = mode === 'stakeholder';
  const steps = isStakeholder ? stakeholderSteps : employeeSteps;
  const navigate = useNavigate();
  const authToken = getAuthToken();
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info' // 'error', 'warning', 'info', 'success'
  });
  // Global loader reference counter to support overlapping uploads
  const [globalLoaderCount, setGlobalLoaderCount] = useState(0);

  const startGlobalLoader = () => {
    setGlobalLoaderCount((count) => {
      if (count === 0) {
        dispatch(showLoader());
      }
      return count + 1;
    });
  };

  const stopGlobalLoader = () => {
    setGlobalLoaderCount((count) => {
      const next = Math.max(0, count - 1);
      if (next === 0) {
        dispatch(dismissLoader());
      }
      return next;
    });
  };
  // Document processing state variables
  const [extractedData, setExtractedData] = useState({
    passportCopy: {},
    visaPage: {},
    emiratesId: {},
    salaryCertificate: {},
    birthCertificate: {}
  });
  // State for extracted data summary dialog
  const [showExtractedDataDialog, setShowExtractedDataDialog] = useState(false);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [processingStatus, setProcessingStatus] = useState({});
  const [processingLock, setProcessingLock] = useState({});
  const processingTimeouts = useRef({});
  const [extractionStatus, setExtractionStatus] = useState({
    passportCopy: { total: 0, extracted: 0 },
    visaPage: { total: 0, extracted: 0 },
    emiratesId: { total: 0, extracted: 0 },
    salaryCertificate: { total: 0, extracted: 0 },
    birthCertificate: { total: 0, extracted: 0 }
  });
  
  // Photo validation dialog state
  const [photoValidationDialog, setPhotoValidationDialog] = useState({
    open: false,
    loading: false,
    file: null,
    result: null,
    error: null,
    timedOut: false,
    validationStartTime: null
  });
  
  // Auto-fix dialog state
  const [photoAutoFixDialog, setPhotoAutoFixDialog] = useState({
    open: false,
    autoFixUrl: null,
    issues: [],
    details: {},
    originalFile: null
  });
  
  // Auto-fix preview dialog state
  const [photoAutoFixPreviewDialog, setPhotoAutoFixPreviewDialog] = useState({
    open: false,
    originalImageUrl: null,
    fixedImageUrl: null,
    loading: false
  });
  
  // Photo input ref
  const photoInputRef = useRef(null);
  
  // Utility function to convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };
  
  // Utility function to convert base64 to blob
  const base64ToBlob = (base64, mimeType) => {
    const byteString = atob(base64.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    
    return new Blob([ab], { type: mimeType });
  };

  // UUID detection helper (used across component)
  const isUUID = (v) => {
    if (!v || typeof v !== 'string') return false;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);
  };

  // Map UI doc types to Document Processing service enum codes (UPPER_SNAKE)
  // Dynamic service fields (UUIDs) are passed through as-is by callers before mapping
  const toProcessingDocumentTypeLocal = (t) => {
    switch (t) {
      case 'passportCopy':
        return 'PASSPORT_COPY';
      case 'visaPage':
      case 'VISA_PAGE':
        return 'VISA_PAGE';
      case 'salaryCertificate':
        return 'SALARY_CERTIFICATE';
      case 'birthCertificate':
        return 'BIRTH_CERTIFICATE';
      case 'emiratesId':
        return 'EMIRATES_ID';
      case 'photo':
        // Orchestrator registers PhotoDocumentProcessor with type 'PHOTO'
        return 'PHOTO';
      default:
        // Fallback: convert camelCase/PascalCase to UPPER_SNAKE
        return (t || 'UNKNOWN')
          .replace(/([a-z])([A-Z])/g, '$1_$2')
          .replace(/\s+/g, '_')
          .toUpperCase();
    }
  };

  // Safely extract a filePath from different field shapes
  // Supports objects like { filePath }, { value: { filePath | documentUrl } }, { documentUrl }, or raw string
  const getFilePathFromField = (field) => {
    if (!field) return null;
    if (typeof field === 'string') return field; // already a path

    // direct property
    if (field.filePath) return field.filePath;

    // nested value
    if (field.value && typeof field.value === 'object') {
      if (field.value.filePath) return field.value.filePath;
      if (field.value.documentUrl) {
        try {
          const u = new URL(field.value.documentUrl, window.location.origin);
          const p = u.searchParams.get('filePath');
          if (p) return decodeURIComponent(p);
        } catch (_) { /* ignore URL parse errors */ }
      }
    }

    // documentUrl at root
    if (field.documentUrl) {
      try {
        const u = new URL(field.documentUrl, window.location.origin);
        const p = u.searchParams.get('filePath');
        if (p) return decodeURIComponent(p);
      } catch (_) { /* ignore URL parse errors */ }
    }

    return null;
  };

  // Build a document URL for viewing
  // For dynamic service fields (UUID docType), use file path download endpoint
  const getDocumentUrl = (docType, field) => {
    if (isUUID(docType)) {
      const filePath = getFilePathFromField(field);
      if (filePath) {
        return `/v1/files/download?filePath=${encodeURIComponent(filePath)}`;
      }
      return null;
    }
    // For static fields, prefer preview/object URL logic handled by callers
    if (field?.preview) return field.preview;
    if (field?.file) return URL.createObjectURL(field.file);
    return null;
  };

  // Centralized updater for processing state and counts
  const updateDocumentProcessingState = (docType, status, message = null, fieldCount = 0, index = null) => {
    // Update processing status
    setProcessingStatus(prev => ({
      ...prev,
      [docType]: { ...(prev[docType] || {}), status, message, index }
    }));

    // Always unlock processing on terminal states
    setProcessingLock(prev => ({
      ...prev,
      [docType]: false
    }));

    // Update extraction status counts (best-effort)
    setExtractionStatus(prev => {
      const current = prev[docType] || { total: 0, extracted: 0 };
      const total = index !== null ? Math.max(current.total, index + 1) : Math.max(current.total, 1);
      const extracted = typeof fieldCount === 'number' ? fieldCount : current.extracted;
      return { ...prev, [docType]: { total, extracted } };
    });
  };

  // Record an upload in the per-category registry with a sequence number
  const recordUpload = (backendDocType, meta) => {
    if (!backendDocType) return;
    setUploadRegistry(prev => {
      const existing = prev[backendDocType] ? [...prev[backendDocType]] : [];
      const sequence = existing.length + 1;
      existing.push({ sequence, ...meta });
      return { ...prev, [backendDocType]: existing };
    });
  };
  
  // Track auto-filled fields from document extraction
  const [autoFilledFields, setAutoFilledFields] = useState({
    passportCopy: [],
    visaPage: [],
    emiratesId: [],
    salaryCertificate: [],
    birthCertificate: []
  });
  
  const [formData, setFormData] = useState({
    firstNameEn: '',
    middleNameEn: '',
    lastNameEn: '',
    firstNameAr: '',
    middleNameAr: '',
    lastNameAr: '',
    dob: '',
    gender: '',
    nationality: '',
    passportNumber: '',
    passportIssue: '',
    passportExpiry: '',
    visaNumber: '',
    visaIssueDate: '',
    visaExpiryDate: '',
    visaType: '',
    profession: null,
    professionId: null,
    emiratesId: '',
    emiratesIdIssueDate: '',
    emiratesIdExpiryDate: '',
    requireOhc: false,
    requireDrivingLicense: false,
    uid: '',
    department: null,
    position: null,
    startDate: '',
    employeeId: '',
    reportsTo: '',
    contractType: '',
    salary: '',
    email: '',
    phone: '',
    mobileNumber: '',
    address: '',
    emergencyContact: '',
    emergencyPhone: '',
    bankName: '',
    accountNumber: '',
    iban: '',
    notes: '',
    status: 'ACTIVE',
    // Stakeholder fields
    employeeRole: '',
    ownershipPercentage: '',
    requiresLogin: false,
    username: '',
    password: '',
    confirmPassword: '',
    accessLevel: 'CUSTOMER_EMPLOYEE',
    
    // Documents
    documents: [],
    uid: '',
    
    // Photo
    photoFile: null,
    photoPreview: null,
    
    // Additional document types
    passportCopyFile: null,
    passportCopyPreview: null,
    visaPageFile: null,
    visaPagePreview: null,
    salaryCertificateFile: null,
    salaryCertificatePreview: null,
    birthCertificateFile: null,
    birthCertificatePreview: null,
    emiratesIdFile: null,
    emiratesIdPreview: null,
  });

  // Registry to track each upload by category with sequence and path
  const [uploadRegistry, setUploadRegistry] = useState({});

  const [errors, setErrors] = useState({});
  const [professionMatchError, setProfessionMatchError] = useState(null);
  
  // Hover preview state
  const [hoverPreview, setHoverPreview] = useState({
    anchorEl: null,
    imageUrl: null,
    docType: null
  });
  
  // Profession search states
  const [professionSearchResults, setProfessionSearchResults] = useState([]);
  const [professionSearchLoading, setProfessionSearchLoading] = useState(false);
  const [professionInputValue, setProfessionInputValue] = useState('');
  const [isProfessionAutoMatched, setIsProfessionAutoMatched] = useState(false);
  const professionSearchTimeoutRef = useRef(null);
  
  // Fetch departments and positions on component mount
  useEffect(() => {
    const fetchDepartmentsAndPositions = async () => {
      try {
        const response = await apiService.get('/v1/departments-positions');
        setDepartments(response.departments || []);
        setPositions(response.positions || []);
      } catch (error) {
        console.error('Error fetching departments and positions:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load departments and positions',
          severity: 'error'
        });
      }
    };
    
    fetchDepartmentsAndPositions();
  }, []);
  
  // Define field mappings based on document type (moved to component level to fix initialization error)
  const fieldMappings = {
    passportCopy: {
      'passportNumber': 'passportNumber',
      'issueDate': 'passportIssue',
      'expiryDate': 'passportExpiry',
      'passportExpiryDate': 'passportExpiry', // Added mapping for passportExpiryDate
      'dateOfBirth': 'dob',
      'nationality': 'nationality',
      'gender': 'gender',
      'placeOfBirth': 'placeOfBirth',
      'fullName': 'fullName', // Will be processed separately for firstName/lastName
    },
    // Updated from 'visaPage' to 'VISA_PAGE' to match backend document type code
    'VISA_PAGE': {
      'visaNumber': 'visaNumber',
      'visaIssueDate': 'visaIssueDate',
      'issueDate': 'visaIssueDate',      // Added mapping for issueDate -> visaIssueDate
      'visaExpiryDate': 'visaExpiryDate',
      'expiryDate': 'visaExpiryDate',    // Added mapping for expiryDate -> visaExpiryDate
      'visaType': 'visaType',
      'profession': 'profession',
      'sponsorName': 'sponsorName'
    },
    // Keep the old mapping for backward compatibility
    'visaPage': {
      'visaNumber': 'visaNumber',
      'visaIssueDate': 'visaIssueDate',
      'issueDate': 'visaIssueDate',      // Added mapping for issueDate -> visaIssueDate
      'visaExpiryDate': 'visaExpiryDate',
      'expiryDate': 'visaExpiryDate',    // Added mapping for expiryDate -> visaExpiryDate
      'visaType': 'visaType',
      'profession': 'profession',
      'sponsorName': 'sponsorName'
    },
    'emiratesId': {
      'emiratesIdNumber': 'emiratesId',
      'emiratesIdExpiryDate': 'emiratesIdExpiryDate',
      'emiratesIdIssueDate': 'emiratesIdIssueDate',
      'issueDate': 'emiratesIdIssueDate',
      'expiryDate': 'emiratesIdExpiryDate',
      'fullName': 'fullName',
      'dateOfBirth': 'dob',
      'gender': 'gender',
      'nationality': 'nationality'
    },
    salaryCertificate: {
      salary: 'salary',
      position: 'position',
      employmentStartDate: 'employmentStartDate'
    }
  };

  // Match profession string to profession entity
  const matchProfession = async (professionStr) => {
    if (!professionStr || professionStr.trim() === '') {
      console.log('No profession string to match');
      return;
    }
    
    try {
      console.log('Matching profession:', professionStr);
      const response = await apiService.get('/v1/professions/match', {
        professionStr: professionStr.trim()
      });
      
      if (response && response.id && response.title) {
        console.log('Profession matched successfully:', response);
        
        // Update form data with matched profession
        setFormData(prev => ({
          ...prev,
          profession: response.title,
          professionId: response.id
        }));
        
        // Update input value for autocomplete
        setProfessionInputValue(response.title);
        
        // Mark as auto-matched (will disable the field)
        setIsProfessionAutoMatched(true);
        
        // Clear any previous error
        setProfessionMatchError(null);
        
        // Fetch profession details to get govt requirements
        await fetchProfessionDetails(response.id);
        
        // Show success notification
        setSnackbar({
          open: true,
          message: `Profession matched: ${response.title}`,
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error matching profession:', error);
      
      // Set error state
      const errorMsg = error.response?.data?.message || 'Failed to match profession. Please select manually.';
      setProfessionMatchError(errorMsg);
      
      // Keep as NOT auto-matched so user can search manually
      setIsProfessionAutoMatched(false);
      
      // Show error notification
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: 'error'
      });
    }
  };

  // Fetch profession details and update govt requirements
  const fetchProfessionDetails = async (professionId) => {
    if (!professionId) {
      console.log('No profession ID to fetch details');
      return;
    }
    
    try {
      console.log('Fetching profession details for ID:', professionId);
      const response = await apiService.get(`/v1/professions/${professionId}`);
      
      if (response) {
        console.log('Profession details fetched:', response);
        
        // Update form data with govt requirements
        const govtReqs = response.govtRequirements || {};
        setFormData(prev => ({
          ...prev,
          requireOhc: govtReqs.ohc === true,
          requireDrivingLicense: govtReqs.drivingLicense === true
        }));
        
        console.log('Updated govt requirements:', {
          requireOhc: govtReqs.ohc === true,
          requireDrivingLicense: govtReqs.drivingLicense === true
        });
      }
    } catch (error) {
      console.error('Error fetching profession details:', error);
      // Don't show error to user, just log it
      // The fields will remain at their current values
    }
  };

  // Search professions with debouncing (minimum 2 characters)
  const searchProfessions = async (searchQuery) => {
    // Clear previous timeout
    if (professionSearchTimeoutRef.current) {
      clearTimeout(professionSearchTimeoutRef.current);
    }

    // Don't search if less than 2 characters
    if (!searchQuery || searchQuery.trim().length < 2) {
      setProfessionSearchResults([]);
      setProfessionSearchLoading(false);
      return;
    }

    // Set loading state
    setProfessionSearchLoading(true);

    // Debounce the API call
    professionSearchTimeoutRef.current = setTimeout(async () => {
      try {
        console.log('Searching professions:', searchQuery);
        const response = await apiService.get('/v1/professions/search', {
          query: searchQuery.trim()
        });

        // Handle response - expecting array of professions
        if (Array.isArray(response)) {
          setProfessionSearchResults(response);
        } else if (response && Array.isArray(response.data)) {
          setProfessionSearchResults(response.data);
        } else {
          setProfessionSearchResults([]);
        }
      } catch (error) {
        console.error('Error searching professions:', error);
        setProfessionSearchResults([]);
        
        // Only show error if it's not a "no results" scenario
        if (error.response?.status !== 404) {
          setSnackbar({
            open: true,
            message: 'Failed to search professions',
            severity: 'error'
          });
        }
      } finally {
        setProfessionSearchLoading(false);
      }
    }, 300); // 300ms debounce delay
  };

  // Handle profession selection from autocomplete
  const handleProfessionSelect = async (event, newValue) => {
    if (newValue) {
      setFormData(prev => ({
        ...prev,
        profession: newValue.title,
        professionId: newValue.id
      }));
      
      // Clear error
      setProfessionMatchError(null);
      
      // Manual selection - NOT auto-matched
      setIsProfessionAutoMatched(false);
      
      // Fetch profession details to get govt requirements
      await fetchProfessionDetails(newValue.id);
      
      console.log('Profession selected manually:', newValue);
    } else {
      // Clear selection
      setFormData(prev => ({
        ...prev,
        profession: null,
        professionId: null,
        requireOhc: false,
        requireDrivingLicense: false
      }));
      
      setIsProfessionAutoMatched(false);
    }
  };

  // Handle form field changes
  const handleChange = (e) => {
    // If e is a string, this is the curried version for select/password fields
    if (typeof e === 'string') {
      const fieldName = e;
      return (event) => {
        const value = event.target.value;
        setFormData(prev => ({
          ...prev,
          [fieldName]: value
        }));
        
        // Clear error for this field when changed
        if (errors[fieldName]) {
          const newErrors = {...errors};
          delete newErrors[fieldName];
          setErrors(newErrors);
        }
      };
    }
    // Otherwise, handle as a regular event object
    else if (e && e.target) {
      const { name: fieldName, value } = e.target;
      setFormData(prev => ({
        ...prev,
        [fieldName]: value
      }));
      
      // Clear error for this field when changed
      if (errors[fieldName]) {
        const newErrors = {...errors};
        delete newErrors[fieldName];
        setErrors(newErrors);
      }
    }
  };

  // No filtering - show all positions
  // (previously filtered by department, but now positions are global)
  const filteredPositions = positions;

  // Handle next step in the wizard
  const handleNext = async () => {
    const currentErrors = validateStep(activeStep);
    
    if (Object.keys(currentErrors).length > 0) {
      setErrors(currentErrors);
      return;
    }
    
    // Additional API validation for Personal Information step (step 0)
    if (activeStep === 0) {
      // Only validate if we have at least one of the fields to check
      if (formData.passportNumber || formData.emiratesId) {
        try {
          setLoading(true);
          
          // Prepare validation payload
          const validationPayload = {
            passportNumber: formData.passportNumber || null,
            emiratesId: formData.emiratesId || null
          };
          
          // Call validation API
          const response = await axios.post(API_ENDPOINTS.EMPLOYEES.VALIDATE_DOC_IDS, validationPayload, {headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          }});
          
          // Check validation response
          const validationErrors = {};
          const validationResult = response.data;
          
          // Check passportNumber validation
          if (validationResult.passportNumber && validationResult.passportNumber.toLowerCase() !== 'valid') {
            validationErrors.passportNumber = validationResult.passportNumber;
          }
          
          // Check emiratesId validation
          if (validationResult.emiratesId && validationResult.emiratesId.toLowerCase() !== 'valid') {
            validationErrors.emiratesId = validationResult.emiratesId;
          }
          
          // If validation errors exist, show them and prevent navigation
          if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('Validation API error:', error);
          setLoading(false);
        } finally {
          setLoading(false);
        }
      }
    }
    
    setActiveStep((prevStep) => prevStep + 1);
    window.scrollTo(0, 0);
  };

  // Handle previous step in the wizard
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    window.scrollTo(0, 0);
  };

  // Handle file uploads for documents and photo
  const handleFileUpload = async (e, docType, index = null) => {
    console.log(`handleFileUpload called for ${docType}${index !== null ? ` at index ${index}` : ''}`);
    // Extract file from event parameter
    const file = e.target.files[0];
    if (!file) {
      console.error(`No file selected for ${docType}`);
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      const errorMsg = `File exceeds the 5MB size limit: ${file.name}`;
      console.error(errorMsg);
      
      setErrors(prev => ({
        ...prev,
        [docType]: errorMsg
      }));
      return;
    }

    // Check file type
    const acceptedTypes = {
      photo: ['image/jpeg', 'image/png', 'image/jpg'],
      passportCopy: ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'],
      visaPage: ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'],
      salaryCertificate: ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'],
      birthCertificate: ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'],
      emiratesId: ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'],
      laborContract: ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'],
      otherDocument: ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf']
    };

    const fileTypes = acceptedTypes[docType] || ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!fileTypes.includes(file.type)) {
      const errorMsg = `Invalid file type: ${file.type}. Accepted types: ${fileTypes.join(', ')}`;
      console.error(errorMsg);
      
      setErrors(prev => ({
        ...prev,
        [docType]: errorMsg
      }));
      return;
    }

    // Clear any previous errors
    if (errors[docType]) {
      const newErrors = { ...errors };
      delete newErrors[docType];
      setErrors(newErrors);
    }

    // Create file preview URL
    const previewUrl = URL.createObjectURL(file);
    console.log(`Created preview URL for ${docType}:`, previewUrl);

    // Create a new document object
    const newDocument = {
      file,
      preview: previewUrl,
      isPrimary: false, // Will be set to true if it's the first document
      uploadedAt: new Date().toISOString()
    };

    // Persist preview/file into form state and track multiple uploads per docType
    setFormData(prev => {
      const updatedData = { ...prev };
      switch (docType) {
        case 'photo':
          // Update photo fields so DocumentUploadField can show file name and preview
          updatedData.photoFile = file;
          updatedData.photoPreview = previewUrl;
          break;
        case 'passportCopy':
          // Update legacy fields for backward compatibility
          updatedData.passportCopyFile = file;
          updatedData.passportCopyPreview = previewUrl;
          // Initialize or update the array of documents
          if (!updatedData.passportCopyFiles) {
            updatedData.passportCopyFiles = [];
          }
          // Add the new document to the array
          const newPassportDoc = { ...newDocument, isPrimary: updatedData.passportCopyFiles.length === 0 };
          updatedData.passportCopyFiles.push(newPassportDoc);
          break;
        case 'visaPage':
          // Update legacy fields for backward compatibility
          updatedData.visaPageFile = file;
          updatedData.visaPagePreview = previewUrl;
          // Initialize or update the array of documents
          if (!updatedData.visaPageFiles) {
            updatedData.visaPageFiles = [];
          }
          // Add the new document to the array
          const newVisaDoc = { ...newDocument, isPrimary: updatedData.visaPageFiles.length === 0 };
          updatedData.visaPageFiles.push(newVisaDoc);
          break;
        case 'salaryCertificate':
          // Update legacy fields for backward compatibility
          updatedData.salaryCertificateFile = file;
          updatedData.salaryCertificatePreview = previewUrl;
          // Initialize or update the array of documents
          if (!updatedData.salaryCertificateFiles) {
            updatedData.salaryCertificateFiles = [];
          }
          // Add the new document to the array
          const newSalaryDoc = { ...newDocument, isPrimary: updatedData.salaryCertificateFiles.length === 0 };
          updatedData.salaryCertificateFiles.push(newSalaryDoc);
          break;
        case 'birthCertificate':
          // Update legacy fields for backward compatibility
          updatedData.birthCertificateFile = file;
          updatedData.birthCertificatePreview = previewUrl;
          // Initialize or update the array of documents
          if (!updatedData.birthCertificateFiles) {
            updatedData.birthCertificateFiles = [];
          }
          // Add the new document to the array
          const newBirthDoc = { ...newDocument, isPrimary: updatedData.birthCertificateFiles.length === 0 };
          updatedData.birthCertificateFiles.push(newBirthDoc);
          break;
        case 'emiratesId':
          // Update legacy fields for backward compatibility
          updatedData.emiratesIdFile = file;
          updatedData.emiratesIdPreview = previewUrl;
          // Initialize or update the array of documents
          if (!updatedData.emiratesIdFiles) {
            updatedData.emiratesIdFiles = [];
          }
          // Add the new document to the array
          const newEmiratesDoc = { ...newDocument, isPrimary: updatedData.emiratesIdFiles.length === 0 };
          updatedData.emiratesIdFiles.push(newEmiratesDoc);
          break;
        default:
          // For other document types, add to documents array
          const existingIndex = updatedData.documents.findIndex(doc => doc.type === docType);
          if (existingIndex >= 0) {
            updatedData.documents[existingIndex] = {
              ...updatedData.documents[existingIndex],
              file,
              preview: previewUrl
            };
          } else {
            updatedData.documents.push({
              type: docType,
              file,
              preview: previewUrl
            });
          }
      }
      
      console.log(`Updated form data for ${docType} with multiple document support`);
      return updatedData;
    });
    // Metadata from immediate upload (if employee UID already exists)
    let uploadedMeta = null;
    const preUploadEmployeeUid = (formData.uid || '').trim();
    if (preUploadEmployeeUid) {
      try {
        const uploadFd = new FormData();
        uploadFd.append('file', file);
        // For storage uploads use backend snake_case documentType
        const uploadDocType = isUUID(docType) ? docType : toBackendDocumentTypeLocal(docType);
        if (uploadDocType) uploadFd.append('documentType', uploadDocType);
        // Set correct fileType for S3 path routing
        uploadFd.append('fileType', docType === 'photo' ? 'PROFILE_IMAGE' : 'USER_DOCUMENT');
        // Append required employeeUid
        uploadFd.append('employeeUid', preUploadEmployeeUid);

        const uploadResp = await apiService.api.post(
          '/v1/files/upload',
          uploadFd,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        if (uploadResp?.data?.success && uploadResp.data.data) {
          const d = uploadResp.data.data;
          let filePath = d.filePath;
          if (filePath && !filePath.startsWith('/')) filePath = '/' + filePath;
          uploadedMeta = {
            filePath,
            fileName: d.originalName || file.name,
            contentType: d.contentType || file.type,
            fileSize: d.fileSize || file.size
          };

          // Persist into formData for both multiple and single doc flows
          setFormData(prev => {
            const updated = { ...prev };
            if (index !== null) {
              const key = `${docType}Files`;
              const arr = Array.isArray(updated[key]) ? [...updated[key]] : [];
              while (arr.length <= index) arr.push({});
              arr[index] = {
                ...arr[index],
                ...uploadedMeta
              };
              updated[key] = arr;
            } else {
              const base = docType;
              updated[`${base}FilePath`] = uploadedMeta.filePath;
              updated[`${base}FileName`] = uploadedMeta.fileName;
              updated[`${base}ContentType`] = uploadedMeta.contentType;
              updated[`${base}FileSize`] = uploadedMeta.fileSize;
            }
            return updated;
          });
        } else {
          console.warn(`Upload did not return filePath for ${docType}`);
        }
      } catch (uploadErr) {
        console.error(`Immediate upload failed for ${docType}${index !== null ? ` (${index + 1})` : ''}:`, uploadErr);
        setSnackbar({
          open: true,
          message: `${docType}${index !== null ? ` (${index + 1})` : ''} upload failed`,
          severity: 'error'
        });
      }
    } else {
      console.log(`No employee UID yet. Deferring ${docType} upload until after employee creation.`);
    }

    // Then: create multipart form payload for the document processing API
    const documentIndex = index;
    const isDynamicServiceField = isUUID(docType);
    const apiDocType = isDynamicServiceField ? docType : toProcessingDocumentTypeLocal(docType);

    const fd = new FormData();
    fd.append('file', file);
    fd.append('documentType', apiDocType);
    if (documentIndex !== null) {
      fd.append('documentIndex', documentIndex);
      console.log(`Sending ${docType} (${apiDocType}) document ${documentIndex + 1} for processing...`);
    } else {
      console.log(`Sending ${docType} (${apiDocType}) for processing...`);
    }

    if (docType === 'emiratesId') {
      console.log(`Processing ${docType} document with file details:`, {
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: new Date(file.lastModified).toISOString(),
        index: documentIndex
      });
    }

    // Execute processing request
    // Show global loader (rotating clock) during extract API call
    startGlobalLoader();

    const extractWithRetries = async (maxRetries = 3, delay = 2000) => {
      let attempt = 0;
      while(attempt < maxRetries){
        try {
          const response = await apiService.api.post(
            API_ENDPOINTS.DOCUMENT_PROCESSING.EXTRACT,
            fd,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              },
              timeout: 60000
            }
          );

          return response;
        } catch(err) {
          attempt++;
          console.warn(`Extraction attempt ${attempt} failed:`, err);

          if(attempt < maxRetries){
            await new Promise(res => setTimeout(res, delay));
          } else {
            throw err;
          }
        }
      }
    };

    try {
      const response = await extractWithRetries(3);
      // Get extracted data and field count
      const newExtractedData = response.data;
      const fieldCount = Object.keys(newExtractedData).length;
      
      // For dynamic service fields, ensure filePath is properly stored
      if (isDynamicServiceField && response.data.filePath) {
        // Store filePath in a consistent location for our helper function to find
        newExtractedData.filePath = response.data.filePath;
        console.log(`Stored filePath for dynamic service field ${docType}:`, response.data.filePath);
      }
      
      // Update state to success with field count
      const successMessage = documentIndex !== null 
        ? `Processed ${docType} document ${documentIndex + 1}` 
        : null;
      updateDocumentProcessingState(docType, 'success', successMessage, fieldCount, documentIndex);
      
      // Ensure photo preview/file persist after processing completes
      if (docType === 'photo') {
        setFormData(prev => ({
          ...prev,
          photoFile: file || prev.photoFile,
          photoPreview: previewUrl || prev.photoPreview
        }));
        
        // Clear the success status for photo so only the preview shows (no success message)
        setTimeout(() => {
          setProcessingStatus(prev => {
            const updated = { ...prev };
            delete updated.photo;
            return updated;
          });
        }, 500); // Small delay to allow status to be registered
      }
      
      // Special handling for specific document types
      if (docType === 'emiratesId') {
        console.log('Emirates ID extraction response:', JSON.stringify(newExtractedData, null, 2));
        
        // Normalize Emirates ID data if needed
        if (newExtractedData.emiratesIdNumber && !newExtractedData.emiratesIdNumber.toString().trim().startsWith('784-')) {
          console.log('Normalizing Emirates ID number format');
          newExtractedData.emiratesIdNumber = newExtractedData.emiratesIdNumber.toString().trim();
        }
        
        // Ensure date fields are properly formatted
        if (newExtractedData.dateOfBirth && !newExtractedData.dateOfBirth.includes('/')) {
          console.log('Normalizing date of birth format');
          // Convert ISO date to MM/DD/YYYY for consistency with other document types
          const date = new Date(newExtractedData.dateOfBirth);
          if (!isNaN(date.getTime())) {
            newExtractedData.dateOfBirth = `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
          }
        }
      }
      
      // Store extracted data - for multiple documents, store in an array under the document type
      if (documentIndex !== null) {
        setExtractedData(prev => {
          // Initialize array if it doesn't exist
          const currentExtractedDataArray = Array.isArray(prev[docType]) ? prev[docType] : [];
          
          // Create a new array with the new data at the specified index
          const newExtractedDataArray = [...currentExtractedDataArray];
          
          // Ensure array has enough elements
          while (newExtractedDataArray.length <= documentIndex) {
            newExtractedDataArray.push({});
          }
          
          // Update the data at the specific index
          const documentData = {
            ...newExtractedData,
            documentIndex: documentIndex,
            isPrimary: documentIndex === 0 // First document is primary by default
          };
          
          // For dynamic service fields, ensure filePath is properly stored in a consistent location
          if (isDynamicServiceField) {
            // Make sure filePath is accessible in multiple locations for backward compatibility
            if (newExtractedData.filePath) {
              documentData.filePath = newExtractedData.filePath;
              console.log(`Stored filePath in documentData for dynamic service field ${docType}:`, newExtractedData.filePath);
            }
          }
          
          newExtractedDataArray[documentIndex] = documentData;
          
          return {
            ...prev,
            [docType]: newExtractedDataArray
          };
        });
        
        console.log(`Extracted data from ${docType} document ${documentIndex + 1}:`, newExtractedData);

        // Also persist filePath and metadata back into formData for the same indexed document
        if (isDynamicServiceField && (newExtractedData.filePath || response.data.filePath)) {
          const fp = newExtractedData.filePath || response.data.filePath;
          const fileName = response.data.fileName || response.data.originalName || file.name;
          const contentType = response.data.contentType || file.type;
          const fileSize = response.data.fileSize || file.size;
          setFormData(prev => {
            const updated = { ...prev };
            const key = `${docType}Files`;
            const arr = Array.isArray(updated[key]) ? [...updated[key]] : [];
            while (arr.length <= documentIndex) arr.push({});
            arr[documentIndex] = {
              ...arr[documentIndex],
              filePath: fp,
              fileName,
              contentType,
              fileSize
            };
            updated[key] = arr;
            return updated;
          });
        }
      } else {
        // Legacy behavior for single document
        setExtractedData(prev => {
          const documentData = {...newExtractedData};
          
          // For dynamic service fields, ensure filePath is properly stored in a consistent location
          if (isDynamicServiceField && newExtractedData.filePath) {
            // Make sure filePath is accessible in multiple locations for backward compatibility
            documentData.filePath = newExtractedData.filePath;
            console.log(`Stored filePath in single document data for dynamic service field ${docType}:`, newExtractedData.filePath);
          }
          
          return {
            ...prev,
            [docType]: documentData
          };
        });
        
        console.log(`Extracted data from ${docType}:`, newExtractedData);

        // Persist filePath and metadata into legacy single fields on formData
        if (isDynamicServiceField && (newExtractedData.filePath || response.data.filePath)) {
          const fp = newExtractedData.filePath || response.data.filePath;
          const fileName = response.data.fileName || response.data.originalName || file.name;
          const contentType = response.data.contentType || file.type;
          const fileSize = response.data.fileSize || file.size;
          setFormData(prev => {
            const updated = { ...prev };
            const base = docType;
            updated[`${base}FilePath`] = fp;
            updated[`${base}FileName`] = fileName;
            updated[`${base}ContentType`] = contentType;
            updated[`${base}FileSize`] = fileSize;
            return updated;
          });
        }
      }
      
      // Check for conflicts with existing data
      checkForConflicts(docType, newExtractedData);
      
      // Auto-fill form fields with extracted data - only use primary document for auto-fill
      // or if no index specified (legacy behavior)
      if (documentIndex === null || documentIndex === 0) {
        autoFillFormFields(docType, newExtractedData);
      }
      
      // Show success notification
      const notificationMessage = documentIndex !== null 
        ? `Successfully extracted ${fieldCount} fields from ${docType} document ${documentIndex + 1}` 
        : `Successfully extracted ${fieldCount} fields from ${docType}`;
      
      setSnackbar({
        open: true,
        message: notificationMessage,
        severity: 'success'
      });
      
      return true;
    } catch (error) {
      // Handle API errors
      console.error(`Error processing ${docType}${documentIndex !== null ? ` document ${documentIndex + 1}` : ''}:`, error);
      
      // Update state to error
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      updateDocumentProcessingState(docType, 'error', errorMessage, 0, documentIndex);
      
      // Show error notification
      setSnackbar({
        open: true,
        message: `Error processing ${docType}${documentIndex !== null ? ` document ${documentIndex + 1}` : ''}: ${errorMessage}`,
        severity: 'error'
      });
    } finally {
      // Always hide the global loader
      stopGlobalLoader();
    }
  };
  
  // Check for conflicts between extracted data and existing form data
  const checkForConflicts = (docType, newData) => {
    const newConflicts = [];
    
    // Helper: normalize values for reliable comparison
    const normalizeForConflict = (value, field) => {
      if (value == null) return '';
      let v = String(value).trim();
      // Field-specific normalization
      switch (field) {
        case 'gender': {
          const g = v.toLowerCase();
          if (['m', 'male'].includes(g)) return 'male';
          if (['f', 'female'].includes(g)) return 'female';
          return g; // fallback
        }
        case 'dateOfBirth':
        case 'passportIssueDate':
        case 'passportExpiryDate':
        case 'visaIssueDate':
        case 'visaExpiryDate':
        case 'employmentStartDate': {
          // Normalize common date formats to yyyy-mm-dd
          // Accept dd/MM/yyyy, MM/dd/yyyy, yyyy-MM-dd
          // If parse fails, return lowercased trimmed string
          const iso = (() => {
            // Replace slashes with dashes
            const s = v.replace(/\//g, '-');
            // If already yyyy-mm-dd
            if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
            // dd-mm-yyyy or mm-dd-yyyy -> attempt Date parsing safely
            const parts = s.split('-');
            if (parts.length === 3) {
              const [a, b, c] = parts.map(p => parseInt(p, 10));
              if (!isNaN(a) && !isNaN(b) && !isNaN(c)) {
                // Heuristic: if a > 31 assume yyyy-mm-dd already caught; if c < 100 assume year two-digit -> fallback
                // Try dd-mm-yyyy first
                const to2 = (n) => String(n).padStart(2, '0');
                if (a <= 31 && b <= 12 && c > 31) {
                  return `${c}-${to2(b)}-${to2(a)}`;
                }
                // Try mm-dd-yyyy
                if (a <= 12 && b <= 31 && c > 31) {
                  return `${c}-${to2(a)}-${to2(b)}`;
                }
              }
            }
            // Last resort: Date parse
            const d = new Date(v);
            if (!isNaN(d.getTime())) {
              const to2 = (n) => String(n).padStart(2, '0');
              return `${d.getFullYear()}-${to2(d.getMonth() + 1)}-${to2(d.getDate())}`;
            }
            return v.toLowerCase();
          })();
          return iso;
        }
        case 'salary': {
          // strip commas/currency and keep numeric
          const num = v.replace(/[^0-9.\-]/g, '');
          return num;
        }
        default:
          return v.toLowerCase();
      }
    };
    
    // Enhanced field mapping for cross-document validation
    const conflictFields = {
      // Personal Information
      firstName: { label: 'First Name', priority: ['passportCopy', 'emiratesId', 'visaPage'] },
      lastName: { label: 'Last Name', priority: ['passportCopy', 'emiratesId', 'visaPage'] },
      dateOfBirth: { label: 'Date of Birth', priority: ['passportCopy', 'birthCertificate', 'emiratesId'] },
      nationality: { label: 'Nationality', priority: ['passportCopy', 'visaPage'] },
      passportNumber: { label: 'Passport Number', priority: ['passportCopy'] },
      gender: { label: 'Gender', priority: ['passportCopy', 'emiratesId'] },
      placeOfBirth: { label: 'Place of Birth', priority: ['passportCopy', 'birthCertificate'] },
      
      // Visa Information
      visaNumber: { label: 'Visa Number', priority: ['visaPage'] },
      visaType: { label: 'Visa Type', priority: ['visaPage'] },
      profession: { label: 'Profession', priority: ['visaPage', 'salaryCertificate'] },
      
      // Emirates ID
      emiratesIdNumber: { label: 'Emirates ID Number', priority: ['emiratesId'] },
      
      // Employment Information
      position: { label: 'Position', priority: ['salaryCertificate'] },
      salary: { label: 'Salary', priority: ['salaryCertificate'] },
      employmentStartDate: { label: 'Employment Start Date', priority: ['salaryCertificate'] }
    };
    
    // Check each field for conflicts
    Object.entries(conflictFields).forEach(([field, fieldInfo]) => {
      // Only check if the new data contains this field
      if (newData[field] && String(newData[field]).trim() !== '') {
        const newValNorm = normalizeForConflict(newData[field], field);
        // Check against existing form data
        if (formData[field] && String(formData[field]).trim() !== '') {
          const formValNorm = normalizeForConflict(formData[field], field);
          if (formValNorm !== newValNorm) {
          
          // Create conflict object with source document info
          newConflicts.push({
            field,
            label: fieldInfo.label,
            currentValue: formData[field],
            newValue: newData[field],
            source: docType,
            priority: fieldInfo.priority.indexOf(docType) // For priority-based resolution
          });
          }
        }
        
        // Enhanced: Check against data extracted from other documents
        Object.entries(extractedData).forEach(([otherDocType, otherDocData]) => {
          // Skip comparing with itself or empty documents
          if (otherDocType !== docType && otherDocData[field] && String(otherDocData[field]).trim() !== '') {
            const otherValNorm = normalizeForConflict(otherDocData[field], field);
            if (otherValNorm !== newValNorm) {
            
            // Determine which document has higher priority for this field
            const currentPriority = fieldInfo.priority.indexOf(docType);
            const otherPriority = fieldInfo.priority.indexOf(otherDocType);
            
            // Only create conflict if the current document has equal or higher priority
            if (currentPriority !== -1 && (otherPriority === -1 || currentPriority <= otherPriority)) {
              newConflicts.push({
                field,
                label: fieldInfo.label,
                currentValue: otherDocData[field],
                newValue: newData[field],
                source: `${docType} vs ${otherDocType}`,
                currentSource: otherDocType,
                newSource: docType,
                priority: currentPriority
              });
            }
            }
          }
        });
      }
    });
    
    // If conflicts found, update state and show dialog
    if (newConflicts.length > 0) {
      // Sort conflicts by priority (if available)
      const sortedConflicts = newConflicts.sort((a, b) => {
        // Sort by field priority first
        if (a.priority !== undefined && b.priority !== undefined) {
          return a.priority - b.priority;
        }
        // Then by field name
        return a.field.localeCompare(b.field);
      });
      
      setConflicts(prev => [...prev, ...sortedConflicts]);
      setShowConflictDialog(true);
    }
  };
  
  // Handle resolving a conflict
  const handleResolveConflict = (field, value) => {
    console.log(`Resolving conflict for field ${field} with value: ${value}`);
    
    // Update form data with the selected value
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Remove this conflict from the list
    setConflicts(prev => prev.filter(conflict => conflict.field !== field));
    
    // Show notification
    setSnackbar({
      open: true,
      message: `Field "${field}" updated with selected value`,
      severity: 'success'
    });
    
    // If all conflicts are resolved, close the dialog
    if (conflicts.length <= 1) {
      setShowConflictDialog(false);
    }
  };
  
  // Auto-fill form fields from extracted document data
  const autoFillFormFields = (docType, extractedData) => {
    if (!extractedData || Object.keys(extractedData).length === 0) {
      console.log(`No data to auto-fill for ${docType}`);
      return;
    }
    
    // Debug logging for Emirates ID extraction
    if (docType === 'emiratesId') {
      console.log('Emirates ID extracted data:', JSON.stringify(extractedData, null, 2));
      console.log('Emirates ID field mapping:', JSON.stringify(fieldMappings.emiratesId, null, 2));
      
      // Log current form data for Emirates ID fields
      console.log('Current form data for Emirates ID fields:');
      console.log('- emiratesId:', formData.emiratesId);
      console.log('- emiratesIdExpiryDate:', formData.emiratesIdExpiryDate);
      console.log('- fullName:', formData.firstNameEn, formData.lastNameEn);
      console.log('- dob:', formData.dob);
      console.log('- gender:', formData.gender);
      console.log('- nationality:', formData.nationality);
    }
    
    console.log(`Auto-filling form fields with data from ${docType}:`, extractedData);
    
    // Update form data with extracted information
    const newFormData = { ...formData };
    
    // Use the field mappings defined at component level
    
    // Get the mapping for the current document type
    const mapping = fieldMappings[docType];
    
    if (mapping) {
      // Debug the extracted data
      console.log('Full extracted data object:', extractedData);
      console.log('Available keys in extracted data:', Object.keys(extractedData));
      
      // Special handling for fullName - split into firstName and lastName
      if (extractedData.fullName && extractedData.fullName.trim() !== '') {
        const nameParts = extractedData.fullName.trim().split(' ');
        if (nameParts.length > 0) {
          // First part is firstName
          const firstName = nameParts[0];
          // Rest is lastName
          const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
          
          // Set firstName if not already set
          if (!newFormData.firstNameEn || newFormData.firstNameEn.trim() === '') {
            newFormData.firstNameEn = firstName;
            console.log(`Auto-filled firstNameEn with value: ${firstName} (from fullName)`);
          }
          
          // Set lastName if not already set
          if (!newFormData.lastNameEn || newFormData.lastNameEn.trim() === '') {
            newFormData.lastNameEn = lastName;
            console.log(`Auto-filled lastNameEn with value: ${lastName} (from fullName)`);
          }
        }
      }
      
      // Apply field mappings
      Object.entries(mapping).forEach(([extractedField, formField]) => {
        // Skip fullName as we've already processed it specially
        if (extractedField === 'fullName') return;
        
        // Skip profession as we'll handle it with API call
        if (extractedField === 'profession' || formField === 'profession') {
          console.log(`Skipping profession field for direct auto-fill, will call match API`);
          return;
        }
        
        console.log(`Checking field mapping: ${extractedField} -> ${formField}`);
        console.log(`Value in extracted data: ${extractedData[extractedField]}`);
        
        // Special debug for passport expiry date
        if (formField === 'passportExpiry' || extractedField === 'expiryDate' || extractedField === 'passportExpiryDate') {
          console.log(`DEBUG PASSPORT EXPIRY: Field ${extractedField} -> ${formField}, Value: ${extractedData[extractedField]}`);
        }
        
        // Special logging for Emirates ID fields
        if (docType === 'emiratesId') {
          console.log(`Processing Emirates ID field: ${extractedField} -> ${formField}`);
          console.log(`Data type: ${typeof extractedData[extractedField]}`);
          console.log(`Has value: ${!!extractedData[extractedField]}`);
          if (extractedData[extractedField]) {
            console.log(`Value: "${extractedData[extractedField]}"`); 
          }
        }
        
        // Improved validation to handle different data types
        const hasValue = extractedData[extractedField] !== null && 
                        extractedData[extractedField] !== undefined && 
                        (typeof extractedData[extractedField] !== 'string' || extractedData[extractedField].trim() !== '');
        
        if (hasValue) {
          // Only update if the field is not already filled or if it's empty
          const fieldIsEmpty = !newFormData[formField] || 
                              (typeof newFormData[formField] === 'string' && newFormData[formField].trim() === '');
          
          if (fieldIsEmpty) {
            // Special handling for gender field (dropdown)
            if (formField === 'gender') {
              // Convert gender value to uppercase for dropdown
              const genderValue = String(extractedData[extractedField]).trim().toUpperCase();
              // Find matching gender option
              const genderOption = GENDERS.find(g => g.label.toUpperCase() === genderValue);
              if (genderOption) {
                newFormData[formField] = genderOption.value;
                console.log(`Auto-filled ${formField} with value: ${genderOption.value} (from ${extractedData[extractedField]})`);
              } else {
                console.log(`Could not map gender value: ${extractedData[extractedField]} to any dropdown option`);
              }
            } 
            // Special handling for Emirates ID number
            else if (formField === 'emiratesId') {
              const emiratesIdValue = String(extractedData[extractedField]).trim();
              newFormData[formField] = emiratesIdValue;
              console.log(`Auto-filled ${formField} with value: ${emiratesIdValue} (from ${extractedField})`);
            }
            // Special handling for date fields
            else if (formField.includes('Date') || formField === 'dob' || formField === 'passportExpiry' || formField === 'passportIssue') {
              try {
                const dateValue = extractedData[extractedField];
                console.log(`Processing date field ${formField} with value: ${dateValue}`);
                
                // Check if already in ISO format (YYYY-MM-DD)
                if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                  console.log(`Date already in ISO format: ${dateValue}`);
                  newFormData[formField] = dateValue;
                  console.log(`Auto-filled ${formField} with ISO value: ${dateValue}`);
                }
                // Check if in MM/DD/YYYY format
                else if (dateValue.includes('/')) {
                  const dateParts = dateValue.split('/');
                  if (dateParts.length === 3) {
                    const month = dateParts[0].padStart(2, '0');
                    const day = dateParts[1].padStart(2, '0');
                    const year = dateParts[2];
                    const formattedDate = `${year}-${month}-${day}`;
                    newFormData[formField] = formattedDate;
                    console.log(`Auto-filled ${formField} with converted value: ${formattedDate} (from ${dateValue})`);
                  } else {
                    console.log(`Invalid date format with slashes: ${dateValue}, expected MM/DD/YYYY`);
                  }
                }
                // Check if in DD/MM/YYYY format
                else if (dateValue.includes('.') || dateValue.includes('-')) {
                  const separator = dateValue.includes('.') ? '.' : '-';
                  const dateParts = dateValue.split(separator);
                  if (dateParts.length === 3) {
                    // Assuming DD/MM/YYYY format
                    const day = dateParts[0].padStart(2, '0');
                    const month = dateParts[1].padStart(2, '0');
                    const year = dateParts[2];
                    const formattedDate = `${year}-${month}-${day}`;
                    newFormData[formField] = formattedDate;
                    console.log(`Auto-filled ${formField} with converted value: ${formattedDate} (from ${dateValue})`);
                  } else {
                    console.log(`Invalid date format with ${separator}: ${dateValue}`);
                  }
                } else {
                  console.log(`Unrecognized date format: ${dateValue}`);
                }
              } catch (error) {
                console.error(`Error formatting date: ${extractedData[extractedField]}`, error);
              }
            }
            // Default handling for other fields
            else {
              newFormData[formField] = extractedData[extractedField];
              console.log(`Auto-filled ${formField} with value: ${extractedData[extractedField]}`);
            }
          } else {
            console.log(`Field ${formField} already has value: ${newFormData[formField]}, extracted value: ${extractedData[extractedField]}`);
          }
        } else {
          console.log(`Field ${extractedField} not found in extracted data or is empty`);
        }
      });

    } else {
      console.log(`No field mapping defined for document type: ${docType}`);
    }
    
    // Special debug for visa document processing
    if (docType === 'VISA_PAGE' || docType === 'visaPage') {
      console.log('VISA DOCUMENT DEBUG:');
      console.log('Document Type:', docType);
      console.log('Extracted Data:', extractedData);
      console.log('Visa Number:', extractedData.visaNumber);
      console.log('Visa Type:', extractedData.visaType);
      console.log('Visa Issue Date:', extractedData.visaIssueDate);
      console.log('Visa Expiry Date:', extractedData.visaExpiryDate);
      console.log('Form Data After Processing:', newFormData);
    }
    
    // Update form data with extracted information (merge to avoid overwriting concurrently updated fields like passportCopyFiles)
    setFormData(prev => {
      const updated = {
        ...prev,
        ...newFormData
      };
      
      // CRITICAL: For photo document type, preserve photo preview and file
      // This ensures the preview doesn't get cleared when auto-filling fields
      if (docType === 'photo') {
        updated.photoFile = prev.photoFile;
        updated.photoPreview = prev.photoPreview;
      }
      
      return updated;
    });
    
    // Handle profession matching for visa documents
    if ((docType === 'VISA_PAGE' || docType === 'visaPage') && extractedData.profession) {
      console.log('Profession extracted from visa document, calling match API');
      matchProfession(extractedData.profession);
    }
    
    // Update auto-filled fields tracking
    const autoFilledFields = Object.entries(mapping || {})
      .filter(([extractedField]) => extractedData[extractedField] && extractedData[extractedField].trim !== undefined && extractedData[extractedField].trim() !== '')
      .map(([_, formField]) => formField);
    
    if (autoFilledFields.length > 0) {
      setAutoFilledFields(prev => ({
        ...prev,
        [docType]: autoFilledFields
      }));
    }
    
    // Clear extracted data
    setExtractedData(prev => {
      const newData = { ...prev };
      delete newData[docType];
      return newData;
    });
    
    // Update form data
    setFormData(prev => {
      const updatedData = { ...prev };
      
      switch (docType) {
        case 'photo':
          // Preserve the photo preview and file after processing so the user can still see it
          // Do NOT clear photoFile/photoPreview here. They will be cleared only when user removes.
          break;
        case 'passportCopy':
          updatedData.passportCopyFile = null;
          updatedData.passportCopyPreview = null;
          break;
        case 'visaPage':
          updatedData.visaPageFile = null;
          updatedData.visaPagePreview = null;
          break;
        case 'salaryCertificate':
          updatedData.salaryCertificateFile = null;
          updatedData.salaryCertificatePreview = null;
          break;
        case 'birthCertificate':
          updatedData.birthCertificateFile = null;
          updatedData.birthCertificatePreview = null;
          break;
        case 'emiratesId':
          updatedData.emiratesIdFile = null;
          updatedData.emiratesIdPreview = null;
          break;
        default:
          // For other document types, remove from documents array
          updatedData.documents = updatedData.documents.filter(doc => doc.type !== docType);
      }
      
      return updatedData;
    });
    
    // Clear any errors
    if (errors[docType]) {
      const newErrors = { ...errors };
      delete newErrors[docType];
      setErrors(newErrors);
    }
  };
  
  // Test backend connectivity
  const testBackendConnectivity = async () => {
    console.log('Testing backend connectivity...');
    try {
      // Use centralized API client so baseURL and interceptors apply
      const response = await apiService.api.get('/v1/health');
      console.log('Backend connectivity test response status:', response.status);
      return true;
    } catch (error) {
      console.error('Backend connectivity test failed:', error);
      return false;
    }
  };

  // Handle photo validation
  const handlePhotoValidation = async (file) => {
    return new Promise(async (resolve, reject) => {
      // Set up timeout for validation (1 minute)
      let timeoutId;
      
      try {
        // First test backend connectivity
        const isBackendReachable = await testBackendConnectivity();
        console.log('Backend reachable:', isBackendReachable);
        
        console.log('handlePhotoValidation called with file:', file.name);
        if (!file) {
          reject(new Error('No file provided'));
          return;
        }

        setPhotoValidationDialog(prev => ({
          ...prev,
          open: true,
          loading: true,
          file,
          result: null,
          error: null,
          timedOut: false,
          validationStartTime: Date.now()
        }));

        // Set up timeout for validation (1 minute)
        timeoutId = setTimeout(() => {
          // Update processing status to timeout
          setProcessingLock(prev => ({ ...prev, photo: false }));
          setProcessingStatus(prev => ({
            ...prev,
            photo: { status: 'timeout', message: 'Photo validation timed out after 1 minute' }
          }));
          
          setPhotoValidationDialog(prev => {
            // Only set timeout if still loading
            if (prev.loading) {
              return {
                ...prev,
                loading: false,
                timedOut: true,
                error: 'Validation timed out after 1 minute'
              };
            }
            return prev;
          });

          // Show auto-fix dialog with timeout message
          setPhotoAutoFixDialog({
            open: true,
            issues: ['Validation timed out after 1 minute'],
            details: {
              timeout: {
                required: 'Photo must be validated within 1 minute. Consider using our AI auto-fix feature.'
              }
            },
            originalFile: file,
            timedOut: true
          });
          
          // Close validation dialog
          setPhotoValidationDialog(prev => ({ ...prev, open: false }));
          
          // Resolve the promise with timeout status
          resolve({ status: 'timeout' });
        }, 60000); // 1 minute timeout

        // Create FormData object and append the file
        const formData = new FormData();
        formData.append('file', file);
        console.log('File appended to FormData:', file.name, file.type, file.size);
      
        // Call backend validation API with correct endpoint using multipart/form-data
        console.log('Calling backend photo validation API with direct backend URL...');
        
        // Try with Axios first
        try {
          const response = await apiService.api.post(
            '/v1/documents/processing/validate-photo',
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }
          );
          
          console.log('Axios response received:', response.status);
          console.log('Response data:', response.data);
          
          // Store response data and status
          const responseData = response.data;
          const responseStatus = response.status;
          
          // Clear timeout since we got a response
          clearTimeout(timeoutId);

          setPhotoValidationDialog(prev => ({
            ...prev,
            loading: false,
            result: responseData
          }));

          // If photo is not supported, show auto-fix dialog
          if (!responseData?.supported) {
            setPhotoAutoFixDialog({
              open: true,
              issues: responseData?.issues || ['Unknown issue'],
              details: responseData?.details || {},
              originalFile: file,
              timedOut: false
            });
            setPhotoValidationDialog(prev => ({ ...prev, open: false }));
            // Release processing lock
            setProcessingLock(prev => ({ ...prev, photo: false }));
            setProcessingStatus(prev => ({
              ...prev,
              photo: { status: 'error', message: 'Photo validation failed. Please fix issues.' }
            }));
          } else {
            // Photo is valid, proceed with upload
            handlePhotoUpload(file);
            setPhotoValidationDialog(prev => ({ ...prev, open: false }));
            // Processing lock will be managed by handlePhotoUpload
          }
        } catch (axiosError) {
          console.error('Photo validation via centralized client failed:', axiosError);
          clearTimeout(timeoutId);
          setPhotoValidationDialog(prev => ({ 
            ...prev, 
            loading: false,
            error: axiosError.response?.data?.message || axiosError.message || 'Error validating photo'
          }));
          // Release processing lock on error
          setProcessingLock(prev => ({ ...prev, photo: false }));
          setProcessingStatus(prev => ({
            ...prev,
            photo: { status: 'error', message: 'Photo validation failed: ' + (axiosError.message || 'Unknown error') }
          }));
        }
      } catch (error) {
        console.error('Unexpected error during photo validation setup/flow:', error);
        if (timeoutId) {
          try { clearTimeout(timeoutId); } catch (_) {}
        }
        setPhotoValidationDialog(prev => ({ 
          ...prev, 
          loading: false,
          error: error.message || 'Unexpected error during photo validation'
        }));
        setProcessingLock(prev => ({ ...prev, photo: false }));
        setProcessingStatus(prev => ({
          ...prev,
          photo: { status: 'error', message: 'Photo validation encountered an unexpected error' }
        }));
        reject(error);
      } finally {
        // Ensure timeout is cleared if still pending
        if (timeoutId) {
          try { clearTimeout(timeoutId); } catch (_) {}
        }
      }
    });
  };

  // Handle applying photo auto-fix
  const handleApplyPhotoAutoFix = async (file) => {
    if (!file) return;

    // Check if this is from a timeout scenario
    const isFromTimeout = photoAutoFixDialog.timedOut;

    // Close auto-fix dialog and open preview dialog
    setPhotoAutoFixDialog(prev => ({ ...prev, open: false }));
    setPhotoAutoFixPreviewDialog(prev => ({
      ...prev,
      open: true,
      originalImage: URL.createObjectURL(file),
      loading: true,
      error: null,
      fromTimeout: isFromTimeout
    }));

    try {
      // Convert file to base64
      const base64 = await fileToBase64(file);
      
      // Call backend auto-fix API
      const response = await apiService.api.post(`/v1/files/auto-fix-photo`, {
        photoBase64: base64,
        fromTimeout: isFromTimeout // Let backend know if this is from a timeout scenario
      });

      // Convert base64 to blob and create URL
      const fixedImageBlob = base64ToBlob(response.data.fixedPhotoBase64, file.type);
      const fixedImageUrl = URL.createObjectURL(fixedImageBlob);

      setPhotoAutoFixPreviewDialog(prev => ({
        ...prev,
        loading: false,
        fixedImage: fixedImageUrl,
        fixedImageBlob
      }));
    } catch (error) {
      console.error('Photo auto-fix error:', error);
      setPhotoAutoFixPreviewDialog(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.message || 'Failed to auto-fix photo'
      }));
    }
  };

  // Handle closing auto-fix dialog
  const handleCloseAutoFixDialog = () => {
    setPhotoAutoFixDialog(prev => ({ ...prev, open: false }));
  };

  // Handle continuing with current photo despite validation issues
  const handleContinueWithCurrentPhoto = (file) => {
    if (!file) return;
    
    // Close auto-fix dialog
    setPhotoAutoFixDialog(prev => ({ ...prev, open: false }));
    
    // Proceed with upload
    handlePhotoUpload(file);
    
    // Show notification
    setSnackbar({
      open: true,
      message: 'Continuing with current photo despite validation issues',
      severity: 'info'
    });
  };
  
  // Handle photo upload
  const handlePhotoUpload = async (file) => {
    if (!file) return;
    
    try {
      // Create form data for file upload
      const formData = new FormData();
      formData.append('file', file);
      // Provide documentType so backend classifies properly (snake_case)
      formData.append('documentType', 'profile_image');
      
      // Upload the photo using centralized API client (interceptors handle baseURL and auth)
      const response = await apiService.api.post(
        '/v1/files/upload',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      // Update form data with the uploaded photo information
      setFormData(prev => ({
        ...prev,
        photoFile: file,
        photoPreview: URL.createObjectURL(file),
        photoDocumentId: response.data.documentId,
        photoFilePath: response.data.filePath
      }));
      
      // Show success notification
      setSnackbar({
        open: true,
        message: 'Photo uploaded successfully',
        severity: 'success'
      });
      
      // Clear any previous errors
      if (errors.photo) {
        const newErrors = { ...errors };
        delete newErrors.photo;
        setErrors(newErrors);
      }
      
      // Clear processing lock and status messages
      setProcessingLock(prev => ({ ...prev, photo: false }));
      setProcessingStatus(prev => ({ ...prev, photo: null }));
      
      return response.data;
    } catch (error) {
      console.error('Photo upload error:', error);
      
      // Show error notification
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to upload photo',
        severity: 'error'
      });
      
      // Set error state
      setErrors(prev => ({
        ...prev,
        photo: error.response?.data?.message || 'Failed to upload photo'
      }));
      
      // Clear processing lock but set error status
      setProcessingLock(prev => ({ ...prev, photo: false }));
      setProcessingStatus(prev => ({
        ...prev,
        photo: { status: 'error', message: 'Upload failed: ' + (error.message || 'Unknown error') }
      }));
      
      return null;
    }
  };
  
  // Handle replacing the photo
  const handleReplacePhoto = () => {
    // Close auto-fix dialog
    setPhotoAutoFixDialog(prev => ({ ...prev, open: false }));
    
    // Trigger file input click
    if (photoInputRef.current) {
      photoInputRef.current.click();
    }
  };
  // Handle removing the photo
  const handleRemovePhoto = () => {
    // If there's a preview URL, revoke it to prevent memory leaks
    if (formData.photoPreview) {
      URL.revokeObjectURL(formData.photoPreview);
    }
    
    console.log('Photo removed');
    
    setFormData(prev => ({
      ...prev,
      photoFile: null,
      photoPreview: null,
      photoFilePath: null,
      photoFileName: null,
      photoContentType: null,
      photoFileSize: null
    }));
    
    // Show notification
    setSnackbar({
      open: true,
      message: 'Employee photo removed',
      severity: 'info'
    });
    
    // Clear any photo-related errors
    if (errors.photo) {
      const newErrors = {...errors};
      delete newErrors.photo;
      setErrors(newErrors);
    }
    
    // Clear processing status and lock for photo
    setProcessingStatus(prev => {
      const updated = { ...prev };
      delete updated.photo;
      return updated;
    });
    
    setProcessingLock(prev => ({
      ...prev,
      photo: false
    }));
  };

  // Remove uploaded document
  const removeDocument = (index) => {
    const updatedDocuments = [...formData.documents];
    updatedDocuments.splice(index, 1);
    setFormData(prev => ({
      ...prev,
      documents: updatedDocuments
    }));
  };

  // Generic remove handler used by upload components
  const handleFileRemove = (docType, index) => {
    // Special-case: photo uses this generic hook from UI
    if (docType === 'photo') {
      return handleRemovePhoto();
    }

    const updateArray = (key) => {
      const current = formData[key] || [];
      if (typeof index === 'number') {
        // Revoke preview URL if present
        const item = current[index];
        try {
          const previewUrl = item?.preview || item?.filePreview;
          if (previewUrl) URL.revokeObjectURL(previewUrl);
        } catch (_) {}
        const next = [...current];
        next.splice(index, 1);
        setFormData(prev => ({ ...prev, [key]: next }));
      } else {
        // If index not provided, clear entire list
        setFormData(prev => ({ ...prev, [key]: [] }));
      }
    };

    switch (docType) {
      case 'passportCopy':
        updateArray('passportCopyFiles');
        break;
      case 'visaPage':
        updateArray('visaPageFiles');
        break;
      case 'birthCertificate':
        updateArray('birthCertificateFiles');
        break;
      case 'emiratesId':
        updateArray('emiratesIdFiles');
        break;
      case 'salaryCertificate':
        updateArray('salaryCertificateFiles');
        break;
      default:
        // no-op for unknown types
        break;
    }

    // Clear any error for this docType
    if (errors[docType]) {
      const newErrors = { ...errors };
      delete newErrors[docType];
      setErrors(newErrors);
    }
  };

  // Add another empty slot for multiple document upload controls
  const handleAddMoreDocuments = (docType) => {
    const appendEmpty = (key) => {
      const list = formData[key] || [];
      const next = [...list, { file: null, preview: null }];
      setFormData(prev => ({ ...prev, [key]: next }));
    };

    switch (docType) {
      case 'passportCopy':
        appendEmpty('passportCopyFiles');
        break;
      case 'visaPage':
        appendEmpty('visaPageFiles');
        break;
      case 'birthCertificate':
        appendEmpty('birthCertificateFiles');
        break;
      case 'emiratesId':
        appendEmpty('emiratesIdFiles');
        break;
      case 'salaryCertificate':
        appendEmpty('salaryCertificateFiles');
        break;
      default:
        break;
    }
  };


// Validate the current step's fields
const validateStep = (step) => {
  const stepErrors = {};
  
  // Validate UID if provided
  if (formData.uid && typeof formData.uid === 'string') {
    const uid = formData.uid.trim();
    if (!/^[a-zA-Z0-9_-]+$/.test(uid)) {
      stepErrors.uid = 'UID must contain only letters, numbers, hyphens, and underscores';
    } else if (uid.length > 50) {
      stepErrors.uid = 'UID must be 50 characters or less';
    }
  }
  
  if (step === 0) {
    // Validate Personal Information
    if (!formData.firstNameEn || typeof formData.firstNameEn !== 'string' || !formData.firstNameEn.trim()) {
      stepErrors.firstNameEn = 'First name is required';
    }
    
    if (!formData.lastNameEn || typeof formData.lastNameEn !== 'string' || !formData.lastNameEn.trim()) {
      stepErrors.lastNameEn = 'Last name is required';
    }
    
    if (!formData.mobileNumber || typeof formData.mobileNumber !== 'string' || !formData.mobileNumber.trim()) {
      stepErrors.mobileNumber = 'Mobile number is required';
    }
    
    if (!formData.dob) {
      stepErrors.dob = 'Date of birth is required';
    }
    
    if (!formData.gender) {
      stepErrors.gender = 'Gender is required';
    }
    
    if (!formData.passportNumber || typeof formData.passportNumber !== 'string' || !formData.passportNumber.trim()) {
      stepErrors.passportNumber = 'Passport Number is required';
    }
    
    if (!formData.passportExpiry) {
      stepErrors.passportExpiry = 'Passport Expiry Date is required';
    }
    
    // Validate visa fields if provided
    if (formData.visaNumber && (!formData.visaExpiryDate || !formData.visaType)) {
      if (!formData.visaExpiryDate) {
        stepErrors.visaExpiryDate = 'Visa Expiry Date is required when Visa Number is provided';
      }
      if (!formData.visaType) {
        stepErrors.visaType = 'Visa Type is required when Visa Number is provided';
      }
    }
    
  } else if (step === 1) {
    if (isStakeholder) {
      // Validate Stakeholder Details
      if (!formData.employeeRole) {
        stepErrors.employeeRole = 'Stakeholder role is required';
      }
    } else {
      // Validate Employment Details
      if (!formData.department) {
        stepErrors.department = 'Department is required';
      }

      if (!formData.position) {
        stepErrors.position = 'Position is required';
      }

      if (!formData.startDate) {
        stepErrors.startDate = 'Start date is required';
      }
    }
  } else if (step === 2) {
    // Validate Contact & Access Information
    if (!formData.email || typeof formData.email !== 'string' || !formData.email.trim()) {
      stepErrors.email = 'Email is required';
    } else if (typeof formData.email === 'string' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      stepErrors.email = 'Please enter a valid email address';
    }
    
    if (formData.requiresLogin) {
      if (!formData.username || typeof formData.username !== 'string' || !formData.username.trim()) {
        stepErrors.username = 'Username is required';
      } else if (typeof formData.username === 'string' && formData.username.length < 4) {
        stepErrors.username = 'Username must be at least 4 characters';
      }
      
      if (!formData.password) {
        stepErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        stepErrors.password = 'Password must be at least 8 characters';
      }
      
      if (formData.password !== formData.confirmPassword) {
        stepErrors.confirmPassword = 'Passwords do not match';
      }
    }
  }
  
  return stepErrors;
};

// Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    console.log('Form data:', formData);
    console.log('Auth token:', authToken ? 'Present' : 'Missing');
    
    // Check if event is properly captured
    console.log('Event object:', event ? 'Present' : 'Missing');
    console.log('Event type:', event?.type);
    console.log('Event target:', event?.target);
    
    setLoading(true);
    setErrors({});
    
    try {
      // Validate current step using validateStep function
      const currentStepErrors = validateStep(activeStep);
      
      if (Object.keys(currentStepErrors).length > 0) {
        setErrors(currentStepErrors);
        setLoading(false);
        return;
      }
      
      // Validate required fields
      const commonRequired = [
        { key: 'firstNameEn', label: 'First Name' },
        { key: 'lastNameEn', label: 'Last Name' },
        { key: 'email', label: 'Email' },
        { key: 'mobileNumber', label: 'Mobile Number' },
        { key: 'passportNumber', label: 'Passport Number' },
        { key: 'passportExpiry', label: 'Passport Expiry Date' }
      ];
      const employeeRequired = [
        { key: 'department', label: 'Department' },
        { key: 'position', label: 'Position' },
      ];
      const stakeholderRequired = [
        { key: 'employeeRole', label: 'Stakeholder Role' },
      ];
      const requiredFields = [
        ...commonRequired,
        ...(isStakeholder ? stakeholderRequired : employeeRequired),
      ];
      const missingFields = requiredFields.filter(field => !formData[field.key]).map(field => field.label);
      
      if (missingFields.length > 0) {
        setErrors({
          submit: `Please fill in all required fields: ${missingFields.join(', ')}`
        });
        setLoading(false);
        return;
      }
      
      // Upload photo and additional documents if available to get the file paths
      let photoFilePath = null;
      let photoFileName = null;
      let photoContentType = null;
      let photoFileSize = null;
      
      // For additional document types
      let passportCopyFilePath = null;
      let passportCopyFileName = null;
      let passportCopyContentType = null;
      let passportCopyFileSize = null;
      
      let visaPageFilePath = null;
      let visaPageFileName = null;
      let visaPageContentType = null;
      let visaPageFileSize = null;
      
      let salaryCertificateFilePath = null;
      let salaryCertificateFileName = null;
      let salaryCertificateContentType = null;
      let salaryCertificateFileSize = null;
      
      let birthCertificateFilePath = null;
      let birthCertificateFileName = null;
      let birthCertificateContentType = null;
      let birthCertificateFileSize = null;
      
      let emiratesIdFilePath = null;
      let emiratesIdFileName = null;
      let emiratesIdContentType = null;
      let emiratesIdFileSize = null;
      
      // Map UI docType to backend documentType codes
      const toBackendDocumentType = (fileType) => {
        switch (fileType) {
          case 'passportCopy':
            return 'PASSPORT_COPY';
          case 'visaPage':
            return 'VISA_PAGE';
          case 'salaryCertificate':
            return 'SALARY_CERTIFICATE';
          case 'birthCertificate':
            return 'BIRTH_CERTIFICATE';
          case 'emiratesId':
            return 'EMIRATES_ID';
          case 'photo':
            return 'PROFILE_IMAGE';
          default:
            return (fileType || 'unknown').toUpperCase();
        }
      };

      // Helper function to upload a file and return its metadata
      const uploadFile = async (file, fileType, index = null) => {
        if (!file) return null;
        
        try {
          // Reuse eagerly uploaded metadata if available to avoid duplicate uploads
          const pickMeta = (fp, fn, ct, fs) => {
            if (!fp) return null;
            const fixedPath = fp.startsWith('/') ? fp : `/${fp}`;
            return {
              filePath: fixedPath,
              fileName: fn || file.name,
              contentType: ct || file.type,
              fileSize: fs || file.size,
              isPrimary: index === 0 || index === null
            };
          };

          // Check per type legacy single fields first
          if (fileType === 'photo' && formData.photoFilePath) {
            const meta = pickMeta(formData.photoFilePath, formData.photoFileName, formData.photoContentType, formData.photoFileSize);
            if (meta) return meta;
          }
          if (fileType === 'passportCopy' && formData.passportCopyFilePath) {
            const meta = pickMeta(formData.passportCopyFilePath, formData.passportCopyFileName, formData.passportCopyContentType, formData.passportCopyFileSize);
            if (meta) return meta;
          }
          if (fileType === 'visaPage' && formData.visaPageFilePath) {
            const meta = pickMeta(formData.visaPageFilePath, formData.visaPageFileName, formData.visaPageContentType, formData.visaPageFileSize);
            if (meta) return meta;
          }
          if (fileType === 'salaryCertificate' && formData.salaryCertificateFilePath) {
            const meta = pickMeta(formData.salaryCertificateFilePath, formData.salaryCertificateFileName, formData.salaryCertificateContentType, formData.salaryCertificateFileSize);
            if (meta) return meta;
          }
          if (fileType === 'birthCertificate' && formData.birthCertificateFilePath) {
            const meta = pickMeta(formData.birthCertificateFilePath, formData.birthCertificateFileName, formData.birthCertificateContentType, formData.birthCertificateFileSize);
            if (meta) return meta;
          }
          if (fileType === 'emiratesId' && formData.emiratesIdFilePath) {
            const meta = pickMeta(formData.emiratesIdFilePath, formData.emiratesIdFileName, formData.emiratesIdContentType, formData.emiratesIdFileSize);
            if (meta) return meta;
          }

          // Then check array entries (multiple docs) for an index match
          const arrKey = `${fileType}Files`;
          if (Array.isArray(formData[arrKey])) {
            const idx = index ?? 0;
            const doc = formData[arrKey][idx];
            if (doc && doc.filePath) {
              const meta = pickMeta(doc.filePath, doc.fileName, doc.contentType, doc.fileSize);
              if (meta) return meta;
            }
          }

          const indexStr = index !== null ? ` (${index + 1})` : '';
          console.log(`Uploading ${fileType}${indexStr} before employee creation`);
          console.log(`${fileType}${indexStr} file name:`, file.name);
          console.log(`${fileType}${indexStr} file size:`, (file.size / 1024).toFixed(2), 'KB');
          console.log(`${fileType}${indexStr} file type:`, file.type);
          
          // Create a FormData object for the file upload
          const fileFormData = new FormData();
          fileFormData.append('file', file);
          // Ensure storage uses snake_case document type
          const storageDocType = toBackendDocumentTypeLocal(fileType);
          if (storageDocType) {
            try { fileFormData.append('documentType', storageDocType); } catch(_) {}
          }
          try { fileFormData.append('fileName', file.name); } catch(_) {}
          try { fileFormData.append('fileType', 'USER_DOCUMENT'); } catch(_) {}
          
          // Perform the actual upload using centralized API client
          const uploadResp = await apiService.api.post(
            '/v1/files/upload',
            fileFormData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
          
          console.log(`${fileType}${indexStr} upload successful. Response:`, uploadResp.data);
          
          // Extract the file path from the response
          if (uploadResp.data && (uploadResp.data.success ? uploadResp.data.data : uploadResp.data)) {
            const fileData = uploadResp.data.data || uploadResp.data;
            let filePath = fileData.filePath;
            const fileName = fileData.originalName || file.name;
            const contentType = fileData.contentType || file.type;
            const fileSize = fileData.fileSize || file.size;
            
            console.log(`${fileType}${indexStr} metadata extracted:`, { filePath, fileName, contentType, fileSize });
            
            // Ensure the file path starts with a forward slash for backend compatibility
            if (filePath && !filePath.startsWith('/')) {
              console.log(`Adding leading slash to ${fileType}${indexStr} file path for backend compatibility`);
              filePath = '/' + filePath;
            }
            
            console.log(`Final ${fileType}${indexStr} file path for employee creation:`, filePath);
            
            return {
              filePath,
              fileName,
              contentType,
              fileSize,
              isPrimary: index === 0 || index === null // First document or single document is primary
            };
          } else {
            throw new Error(`File path not returned from ${fileType}${indexStr} upload response`);
          }
        } catch (error) {
          console.error(`Error pre-uploading ${fileType}${index !== null ? ` (${index + 1})` : ''}:`, error);
          console.error('Error details:', error.response ? error.response.data : error.message || 'No response data');
          
          // Show error notification but continue with employee creation
          setSnackbar({
            open: true,
            message: `${fileType}${index !== null ? ` (${index + 1})` : ''} upload failed, but continuing with employee creation...`,
            severity: 'warning'
          });
          
          return null;
        }
      };
      
      // Helper function to upload multiple files of the same type
      const uploadMultipleFiles = async (files, fileType) => {
        if (!files || !Array.isArray(files) || files.length === 0) {
          console.log(`uploadMultipleFiles called for ${fileType} with no files`);
          return [];
        }
        
        console.log(`Uploading ${files.length} ${fileType} files in parallel`);
        console.log(`${fileType} files debug sample[0]:`, files[0] ? { hasFile: !!files[0].file, name: files[0].file?.name, size: files[0].file?.size } : 'n/a');
        
        try {
          // Upload all files in parallel
          const uploadPromises = files.map((doc, index) => {
            if (!doc || !doc.file) {
              console.warn(`Skipping ${fileType} index ${index} due to missing file object`, doc);
              return Promise.resolve(null);
            }
            return uploadFile(doc.file, fileType, index);
          });
          const results = await Promise.all(uploadPromises);
          
          // Filter out any failed uploads
          const successfulUploads = results.filter(result => result !== null);
          console.log(`Successfully uploaded ${successfulUploads.length} of ${files.length} ${fileType} files`);
          
          return successfulUploads;
        } catch (error) {
          console.error(`Error uploading multiple ${fileType} files:`, error);
          return [];
        }
      };
      
      // Show a temporary notification that documents are being uploaded (only if pre-uploading with known UID)
      const hasArrayDocs = (formData.passportCopyFiles && formData.passportCopyFiles.length > 0) ||
                           (formData.visaPageFiles && formData.visaPageFiles.length > 0) ||
                           (formData.salaryCertificateFiles && formData.salaryCertificateFiles.length > 0) ||
                           (formData.birthCertificateFiles && formData.birthCertificateFiles.length > 0) ||
                           (formData.emiratesIdFiles && formData.emiratesIdFiles.length > 0);
      const canPreUpload = (formData.uid || '').trim().length > 0; // only pre-upload if UID known
      if (canPreUpload && (formData.photoFile || formData.passportCopyFile || formData.visaPageFile || 
          formData.salaryCertificateFile || formData.birthCertificateFile || formData.emiratesIdFile || hasArrayDocs)) {
        setSnackbar({
          open: true,
          message: 'Uploading employee documents...',
          severity: 'info'
        });
      }

      // Debug: log what we are about to upload
      console.log('Upload pre-checks:');
      console.log(' - photoFile present:', !!formData.photoFile);
      console.log(' - passportCopyFile present:', !!formData.passportCopyFile, ' | passportCopyFiles length:', (formData.passportCopyFiles || []).length);
      console.log(' - visaPageFile present:', !!formData.visaPageFile, ' | visaPageFiles length:', (formData.visaPageFiles || []).length);
      console.log(' - salaryCertificateFile present:', !!formData.salaryCertificateFile, ' | salaryCertificateFiles length:', (formData.salaryCertificateFiles || []).length);
      console.log(' - birthCertificateFile present:', !!formData.birthCertificateFile, ' | birthCertificateFiles length:', (formData.birthCertificateFiles || []).length);
      console.log(' - emiratesIdFile present:', !!formData.emiratesIdFile, ' | emiratesIdFiles length:', (formData.emiratesIdFiles || []).length);
      
      // Pre-upload selected documents only if UID is already known (custom UID)
      let photoMetadata = null;
      let passportCopyMetadataArray = [];
      let visaPageMetadataArray = [];
      let salaryCertificateMetadataArray = [];
      let birthCertificateMetadataArray = [];
      let emiratesIdMetadataArray = [];

      if (canPreUpload) {
        // Upload all documents in parallel for better performance
        const photoUploadPromise = uploadFile(formData.photoFile, 'photo');
        const passportCopyUploadPromise = formData.passportCopyFiles && formData.passportCopyFiles.length > 0 ?
          uploadMultipleFiles(formData.passportCopyFiles, 'passportCopy') :
          uploadFile(formData.passportCopyFile, 'passportCopy').then(result => result ? [result] : []);
        const visaPageUploadPromise = formData.visaPageFiles && formData.visaPageFiles.length > 0 ?
          uploadMultipleFiles(formData.visaPageFiles, 'visaPage') :
          uploadFile(formData.visaPageFile, 'visaPage').then(result => result ? [result] : []);
        const salaryCertificateUploadPromise = formData.salaryCertificateFiles && formData.salaryCertificateFiles.length > 0 ?
          uploadMultipleFiles(formData.salaryCertificateFiles, 'salaryCertificate') :
          uploadFile(formData.salaryCertificateFile, 'salaryCertificate').then(result => result ? [result] : []);
        const birthCertificateUploadPromise = formData.birthCertificateFiles && formData.birthCertificateFiles.length > 0 ?
          uploadMultipleFiles(formData.birthCertificateFiles, 'birthCertificate') :
          uploadFile(formData.birthCertificateFile, 'birthCertificate').then(result => result ? [result] : []);
        const emiratesIdUploadPromise = formData.emiratesIdFiles && formData.emiratesIdFiles.length > 0 ?
          uploadMultipleFiles(formData.emiratesIdFiles, 'emiratesId') :
          uploadFile(formData.emiratesIdFile, 'emiratesId').then(result => result ? [result] : []);

        const results = await Promise.all([
          photoUploadPromise,
          passportCopyUploadPromise,
          visaPageUploadPromise,
          salaryCertificateUploadPromise,
          birthCertificateUploadPromise,
          emiratesIdUploadPromise
        ]);

        photoMetadata = results[0];
        passportCopyMetadataArray = results[1];
        visaPageMetadataArray = results[2];
        salaryCertificateMetadataArray = results[3];
        birthCertificateMetadataArray = results[4];
        emiratesIdMetadataArray = results[5];
      } else {
        console.log('UID not provided. Skipping pre-submit uploads; will upload after employee creation.');
      }
      
      // For backward compatibility, use the first item in each array as the primary document
      const passportCopyMetadata = passportCopyMetadataArray.length > 0 ? passportCopyMetadataArray[0] : null;
      const visaPageMetadata = visaPageMetadataArray.length > 0 ? visaPageMetadataArray[0] : null;
      const salaryCertificateMetadata = salaryCertificateMetadataArray.length > 0 ? salaryCertificateMetadataArray[0] : null;
      const birthCertificateMetadata = birthCertificateMetadataArray.length > 0 ? birthCertificateMetadataArray[0] : null;
      const emiratesIdMetadata = emiratesIdMetadataArray.length > 0 ? emiratesIdMetadataArray[0] : null;
      
      // Extract metadata from results
      if (photoMetadata) {
        photoFilePath = photoMetadata.filePath;
        photoFileName = photoMetadata.fileName;
        photoContentType = photoMetadata.contentType;
        photoFileSize = photoMetadata.fileSize;
      }
      
      if (passportCopyMetadata) {
        passportCopyFilePath = passportCopyMetadata.filePath;
        passportCopyFileName = passportCopyMetadata.fileName;
        passportCopyContentType = passportCopyMetadata.contentType;
        passportCopyFileSize = passportCopyMetadata.fileSize;
      }
      
      if (visaPageMetadata) {
        visaPageFilePath = visaPageMetadata.filePath;
        visaPageFileName = visaPageMetadata.fileName;
        visaPageContentType = visaPageMetadata.contentType;
        visaPageFileSize = visaPageMetadata.fileSize;
      }
      
      if (salaryCertificateMetadata) {
        salaryCertificateFilePath = salaryCertificateMetadata.filePath;
        salaryCertificateFileName = salaryCertificateMetadata.fileName;
        salaryCertificateContentType = salaryCertificateMetadata.contentType;
        salaryCertificateFileSize = salaryCertificateMetadata.fileSize;
      }
      
      if (birthCertificateMetadata) {
        birthCertificateFilePath = birthCertificateMetadata.filePath;
        birthCertificateFileName = birthCertificateMetadata.fileName;
        birthCertificateContentType = birthCertificateMetadata.contentType;
        birthCertificateFileSize = birthCertificateMetadata.fileSize;
      }
      
      // Extract Emirates ID metadata
      if (emiratesIdMetadata) {
        emiratesIdFilePath = emiratesIdMetadata.filePath;
        emiratesIdFileName = emiratesIdMetadata.fileName;
        emiratesIdContentType = emiratesIdMetadata.contentType;
        emiratesIdFileSize = emiratesIdMetadata.fileSize;
      }
      
      // Update notification if any documents were uploaded
      if (canPreUpload && (photoMetadata || passportCopyMetadata || visaPageMetadata || 
          salaryCertificateMetadata || birthCertificateMetadata || emiratesIdMetadata)) {
        setSnackbar({
          open: true,
          message: 'Documents uploaded successfully, creating employee...',
          severity: 'success'
        });
      }
      
      // Format the date correctly for API
      let startDate = null;
      if (formData.startDate) {
        startDate = new Date(formData.startDate);
      }
      
      // Prepare the request payload
      const employeeCreationRequest = {
        // Include custom UID if provided
        uid: formData.uid ? formData.uid.trim() : null,
        
        // Personal Information
        personalInfo: {
          firstName: formData.firstNameEn,
          middleName: formData.middleNameEn || null,
          lastName: formData.lastNameEn,
          gender: formData.gender,
          dateOfBirth: formData.dob ? new Date(formData.dob).toISOString().split('T')[0] : null,
          mobileNumber: formData.mobileNumber,
          passportNumber: formData.passportNumber,
          // Only send passportIssue if it is strictly in YYYY-MM-DD format; otherwise send null
          passportIssue: /^\d{4}-\d{2}-\d{2}$/.test((formData.passportIssue || '').trim()) ? (formData.passportIssue || '').trim() : null,
          passportExpiry: formData.passportExpiry ? new Date(formData.passportExpiry).toISOString().split('T')[0] : null,
          emiratesId: formData.emiratesId || null,
          emiratesIdIssueDate: formData.emiratesIdIssueDate ? new Date(formData.emiratesIdIssueDate).toISOString().split('T')[0] : null,
          emiratesIdExpiryDate: formData.emiratesIdExpiryDate ? new Date(formData.emiratesIdExpiryDate).toISOString().split('T')[0] : null,
          visaIssueDate: formData.visaIssueDate ? new Date(formData.visaIssueDate).toISOString().split('T')[0] : null,
          visaExpiryDate: formData.visaExpiryDate ? new Date(formData.visaExpiryDate).toISOString().split('T')[0] : null,
          visaType: formData.visaType || null,
          visaSponsor: formData.visaSponsor || null,
          professionId: formData.professionId || null,
          requireOhc: formData.requireOhc || false,
          requireDrivingLicense: formData.requireDrivingLicense || false,
          nationality: formData.nationality || null,
        },

        // Employment Details (omitted for stakeholders)
        ...(isStakeholder ? {} : {
          employmentInfo: {
            department: formData.department ? {
              id: formData.department.id,
              departmentName: formData.department.departmentName
            } : null,
            position: formData.position ? {
              id: formData.position.id,
              positionName: formData.position.positionName
            } : null,
            startDate: startDate ? startDate.toISOString().split('T')[0] : null,
            employeeId: formData.employeeId || `EMP-${Math.floor(Math.random() * 10000)}`,
            reportsTo: formData.reportsTo || null,
            contractType: formData.contractType || null,
            status: formData.status || 'ACTIVE'
          },
        }),
        // Stakeholder fields
        employeeRole: isStakeholder ? formData.employeeRole : 'EMPLOYEE',
        ownershipPercentage: isStakeholder ? formData.ownershipPercentage : null,
        // Contact & Access Information
        contactInfo: {
          email: formData.email,
          address: formData.address || null,
          poBox: formData.poBox || null
        },
        // User account details - only include if login is required
        ...(formData.requiresLogin && {
          userAccess: {
            username: formData.username,
            password: formData.password,
            accessLevel: formData.accessLevel
          },
        }),
        // Process document uploads if any
        documents: formData.documents.map(doc => ({
          type: doc.type,
          name: doc.name,
          content: doc.file ? URL.createObjectURL(doc.file) : null
        })),
        // Include photo and document information if available (only for pre-upload case)
        photo: canPreUpload && photoFilePath ? {
          filePath: photoFilePath,
          fileName: photoFileName,
          contentType: photoContentType,
          fileSize: photoFileSize
        } : null,
        // Include single object references (primary) matching backend format
        // Also include extracted passport metadata if available
        passportCopy: canPreUpload && passportCopyFilePath ? {
          filePath: passportCopyFilePath,
          fileName: passportCopyFileName,
          contentType: passportCopyContentType,
          fileSize: passportCopyFileSize,
          // optional extracted fields
          documentNumber: (extractedData?.passportCopy?.documentNumber) || (formData.passportNumber || null),
          issueDate: extractedData?.passportCopy?.issueDate || null,
          expiryDate: (
            extractedData?.passportCopy?.expiryDate ||
            extractedData?.passportCopy?.passportExpiryDate ||
            (formData.passportExpiry ? new Date(formData.passportExpiry).toISOString().split('T')[0] : null)
          ),
          metadataJson: (() => {
            try {
              const meta = extractedData?.passportCopy;
              return meta && Object.keys(meta).length ? JSON.stringify(meta) : null;
            } catch (_) { return null; }
          })()
        } : null,
        visaPage: canPreUpload && visaPageFilePath ? {
          filePath: visaPageFilePath,
          fileName: visaPageFileName,
          contentType: visaPageContentType,
          fileSize: visaPageFileSize
        } : null,
        salaryCertificate: canPreUpload && salaryCertificateFilePath ? {
          filePath: salaryCertificateFilePath,
          fileName: salaryCertificateFileName,
          contentType: salaryCertificateContentType,
          fileSize: salaryCertificateFileSize
        } : null,
        birthCertificate: canPreUpload && birthCertificateFilePath ? {
          filePath: birthCertificateFilePath,
          fileName: birthCertificateFileName,
          contentType: birthCertificateContentType,
          fileSize: birthCertificateFileSize
        } : null,
        emiratesId: canPreUpload && emiratesIdFilePath ? {
          filePath: emiratesIdFilePath,
          fileName: emiratesIdFileName,
          contentType: emiratesIdContentType,
          fileSize: emiratesIdFileSize
        } : null
      };
      
      // Detailed logging for debugging
      console.log('Sending employee creation request to:', API_ENDPOINTS.EMPLOYEES.CREATE);
      console.log('Auth token present:', !!authToken);
      
      // Validate document information
      const validateDocumentInfo = (docType, filePath, fileName, contentType, fileSize) => {
        if (filePath && typeof filePath === 'string') {
          console.log(`${docType} information details:`);
          console.log(`- filePath:`, filePath);
          console.log(`- fileName:`, fileName);
          console.log(`- contentType:`, contentType);
          console.log(`- fileSize:`, fileSize);
          
          // Validate path format
          if (!filePath.startsWith('/')) {
            console.warn(`Warning: ${docType} file path does not start with /, this might cause issues`);
          }
          return true;
        }
        return false;
      };
      
      // Validate all document types
      const hasPhoto = validateDocumentInfo('Photo', photoFilePath, photoFileName, photoContentType, photoFileSize);
      const hasPassportCopy = validateDocumentInfo('PassportCopy', passportCopyFilePath, passportCopyFileName, passportCopyContentType, passportCopyFileSize);
      const hasVisaPage = validateDocumentInfo('VisaPage', visaPageFilePath, visaPageFileName, visaPageContentType, visaPageFileSize);
      const hasSalaryCertificate = validateDocumentInfo('SalaryCertificate', salaryCertificateFilePath, salaryCertificateFileName, salaryCertificateContentType, salaryCertificateFileSize);
      const hasBirthCertificate = validateDocumentInfo('BirthCertificate', birthCertificateFilePath, birthCertificateFileName, birthCertificateContentType, birthCertificateFileSize);
      const hasEmiratesId = validateDocumentInfo('EmiratesID', emiratesIdFilePath, emiratesIdFileName, emiratesIdContentType, emiratesIdFileSize);
      
      // Log document inclusion status
      console.log('Employee creation payload includes:');
      console.log('- Photo:', hasPhoto);
      console.log('- Passport Copy:', hasPassportCopy);
      console.log('- Visa Page:', hasVisaPage);
      console.log('- Salary Certificate:', hasSalaryCertificate);
      console.log('- Birth Certificate:', hasBirthCertificate);
      console.log('- Profession ID:', formData.professionId);
      console.log('- Profession Title:', formData.profession);
      
      console.log('Full employee creation payload:', JSON.stringify(employeeCreationRequest, null, 2));
      
      try {
        console.log('About to make employee create POST via apiService...');
        // Use configured axios instance so baseURL points to apid and auth is auto-attached
        const response = await apiService.api.post(
          API_ENDPOINTS.EMPLOYEES.CREATE,
          employeeCreationRequest,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
        console.log('Axios POST request completed successfully');
        console.log('Response status:', response.status);
        console.log('Response data:', response.data);
      
        // Handle successful response
        if (response.status === 201 || response.status === 200) {
          // Log the full response structure to debug
          console.log('Employee creation successful. Full response:', response);
          console.log('Response data structure:', response.data);
          
          // The response structure is: { success: true, message: "...", data: { id, employeeId, uid, ... } }
          const employeeData = response.data && response.data.data ? response.data.data : null;
          console.log('Employee data from response:', employeeData);
          
          // Log all possible ID fields to determine which one to use
          if (employeeData) {
            console.log('Employee numeric ID:', employeeData.id);
            console.log('Employee string ID:', employeeData.employeeId);
            console.log('Employee UID:', employeeData.uid);
            
            // Phase 1.5: If we skipped pre-uploads, upload now using the created employee UID
            if (!canPreUpload) {
              try {
                const createdUid = employeeData.uid || employeeData.employeeId;
                if (createdUid) {
                  console.log(`Uploading documents after employee creation for UID ${createdUid}`);

                  const doSingleUpload = async (fileObj, type) => {
                    if (!fileObj) return null;
                    const fd = new FormData();
                    fd.append('file', fileObj);
                    const upDocType = isUUID(type) ? type : toBackendDocumentTypeLocal(type);
                    if (upDocType) fd.append('documentType', upDocType);
                    fd.append('fileType', type === 'photo' ? 'PROFILE_IMAGE' : 'USER_DOCUMENT');
                    fd.append('employeeUid', createdUid);
                    const resp = await apiService.api.post(
                      '/v1/files/upload',
                      fd,
                      { headers: { 'Content-Type': 'multipart/form-data' } }
                    );
                    if (resp?.data?.success && resp.data.data) {
                      const d = resp.data.data;
                      let fp = d.filePath;
                      if (fp && !fp.startsWith('/')) fp = '/' + fp;
                      return {
                        filePath: fp,
                        fileName: d.originalName || fileObj.name,
                        contentType: d.contentType || fileObj.type,
                        fileSize: d.fileSize || fileObj.size,
                        isPrimary: false
                      };
                    }
                    return null;
                  };

                  const doMultiUploads = async (arr, type) => {
                    const results = [];
                    for (let i = 0; i < (arr || []).length; i++) {
                      const meta = await doSingleUpload(arr[i]?.file, type);
                      if (meta) {
                        // first becomes primary
                        results.push({ ...meta, isPrimary: results.length === 0 });
                      }
                    }
                    return results;
                  };

                  // Execute uploads
                  const photoMeta = await doSingleUpload(formData.photoFile, 'photo');
                  const passportArr = formData.passportCopyFiles && formData.passportCopyFiles.length > 0
                    ? await doMultiUploads(formData.passportCopyFiles, 'passportCopy')
                    : (await doSingleUpload(formData.passportCopyFile, 'passportCopy')) ? [await doSingleUpload(formData.passportCopyFile, 'passportCopy')] : [];
                  const visaArr = formData.visaPageFiles && formData.visaPageFiles.length > 0
                    ? await doMultiUploads(formData.visaPageFiles, 'visaPage')
                    : (await doSingleUpload(formData.visaPageFile, 'visaPage')) ? [await doSingleUpload(formData.visaPageFile, 'visaPage')] : [];
                  const salaryArr = formData.salaryCertificateFiles && formData.salaryCertificateFiles.length > 0
                    ? await doMultiUploads(formData.salaryCertificateFiles, 'salaryCertificate')
                    : (await doSingleUpload(formData.salaryCertificateFile, 'salaryCertificate')) ? [await doSingleUpload(formData.salaryCertificateFile, 'salaryCertificate')] : [];
                  const birthArr = formData.birthCertificateFiles && formData.birthCertificateFiles.length > 0
                    ? await doMultiUploads(formData.birthCertificateFiles, 'birthCertificate')
                    : (await doSingleUpload(formData.birthCertificateFile, 'birthCertificate')) ? [await doSingleUpload(formData.birthCertificateFile, 'birthCertificate')] : [];
                  const emiratesArr = formData.emiratesIdFiles && formData.emiratesIdFiles.length > 0
                    ? await doMultiUploads(formData.emiratesIdFiles, 'emiratesId')
                    : (await doSingleUpload(formData.emiratesIdFile, 'emiratesId')) ? [await doSingleUpload(formData.emiratesIdFile, 'emiratesId')] : [];

                  // Build items and bulk save (static employee documents only)
                  const toItems = (arr, type) => (arr || [])
                    .filter(meta => meta && meta.filePath)
                    .map(meta => ({
                      documentType: type,
                      filePath: meta.filePath,
                      fileName: meta.fileName,
                      contentType: meta.contentType,
                      fileSize: meta.fileSize,
                      isPrimary: !!meta.isPrimary
                    }));

                  const items = [
                    ...toItems(passportArr, 'passport_copy'),
                    ...toItems(visaArr, 'visa_page'),
                    ...toItems(salaryArr, 'salary_certificate'),
                    ...toItems(birthArr, 'birth_certificate'),
                    ...toItems(emiratesArr, 'emirates_id')
                  ];

                  if (items.length > 0) {
                    console.log(`Saving ${items.length} employee documents (post-create) for UID ${createdUid}`);
                    await apiService.api.post(
                      API_ENDPOINTS.EMPLOYEE_DOCUMENTS.BULK(createdUid),
                      items
                    );
                  }

                  // Photo is uploaded as PROFILE_IMAGE above; backend associates via path
                }
              } catch (postUploadErr) {
                console.warn('Post-creation upload failed:', postUploadErr);
              }
            }

            // Phase 2: Try bulk-saving employee documents to new storage (feature-flagged on backend)
            try {
              const employeeUid = employeeData.uid || employeeData.employeeId;
              if (employeeUid) {
                // Build items from uploaded metadata arrays (only include those with filePath)
                const toItems = (arr, type) => (arr || [])
                  .filter(meta => meta && meta.filePath)
                  .map(meta => ({
                    documentType: type,
                    filePath: meta.filePath,
                    fileName: meta.fileName,
                    contentType: meta.contentType,
                    fileSize: meta.fileSize,
                    isPrimary: !!meta.isPrimary
                  }));

                // Map legacy doc keys to backend codes
                const items = [
                  ...toItems(passportCopyMetadataArray, 'passport_copy'),
                  ...toItems(visaPageMetadataArray, 'visa_page'),
                  ...toItems(salaryCertificateMetadataArray, 'salary_certificate'),
                  ...toItems(birthCertificateMetadataArray, 'birth_certificate'),
                  ...toItems(emiratesIdMetadataArray, 'emirates_id')
                ];

                if (items.length > 0) {
                  console.log(`Saving ${items.length} employee documents to new storage for UID ${employeeUid}`);
                  await apiService.api.post(
                    API_ENDPOINTS.EMPLOYEE_DOCUMENTS.BULK(employeeUid),
                    items,
                    { headers: { 'Content-Type': 'application/json' } }
                  );
                  console.log('Bulk employee document save completed');
                } else {
                  console.log('No employee document items to bulk save');
                }
              } else {
                console.warn('No employee UID available to bulk save documents');
              }
            } catch (bulkErr) {
              // Gracefully handle feature flag disabled (503) or any errors
              const status = bulkErr?.response?.status;
              const msg = bulkErr?.response?.data?.message || bulkErr?.message;
              if (status === 503) {
                console.log('Employee document new storage disabled (503). Skipping bulk save.');
              } else {
                console.warn('Bulk save of employee documents failed:', { status, msg });
              }
            }
            
            // Show success notification
            setSnackbar({
              open: true,
              message: photoFilePath ? 
                'Employee created successfully with photo' : 
                'Employee created successfully',
              severity: 'success'
            });
          } else {
            console.log('No employee data found in response');
            
            // Show generic success notification
            setSnackbar({
              open: true,
              message: 'Employee created successfully',
              severity: 'success'
            });
          }
        }
        
        // Navigate to the employee list with success message
        const listRoute = isStakeholder ? ROUTES.CUSTOMER.STAKEHOLDERS.LIST : ROUTES.CUSTOMER.EMPLOYEES.LIST;
        navigate(listRoute, {
          state: {
            notification: {
              type: 'success',
              message: isStakeholder ? 'Stakeholder created successfully' : 'Employee created successfully'
            }
          }
        });
      } catch (error) {
      console.error('Error creating employee:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      // Extract error message from response if available
      let errorMessage = 'Failed to create employee. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Set error state
      setErrors({
        submit: errorMessage
      });
      
      // Show error notification
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      }
    } catch (outerError) {
      console.error('Outer error in employee creation process:', outerError);
      
      // Set error state
      setErrors({
        submit: outerError.message || 'An unexpected error occurred'
      });
      
      // Show error notification
      setSnackbar({
        open: true,
        message: outerError.message || 'An unexpected error occurred',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Cancel creation and go back to list
  const handleCancel = () => {
    navigate(isStakeholder ? ROUTES.CUSTOMER.STAKEHOLDERS.LIST : ROUTES.CUSTOMER.EMPLOYEES.LIST);
  };

  // Render content based on active step
  const getStepContent = (step) => {
    switch (step) {
      case 0: // Personal Information
        return (
          <Grid container spacing={2}>

            {/* Enhanced Photo Upload Section - Compact */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2, mb: 2, border: '1px solid #1976d2', boxShadow: '0 2px 4px rgba(0,0,0,0.06)' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <PersonIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
                  <Typography variant="h6" color="primary">{isStakeholder ? 'Stakeholder Photo' : 'Employee Photo'}</Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <strong>Important:</strong> Please upload a clear photo of the employee. This photo will be used for identification purposes.
                </Typography>
                
                <DocumentUploadField
                  documentType="photo"
                  label=""
                  onFileSelect={(e) => handleFileUpload(e, 'photo')}
                  file={
                    formData.photoFile || (
                      formData.photoFilePath ? {
                        filePath: formData.photoFilePath,
                        fileName: formData.photoFileName || 'photo',
                        fileSize: formData.photoFileSize,
                        contentType: formData.photoContentType
                      } : null
                    )
                  }
                  previewUrl={
                    formData.photoPreview 
                      || (formData.photoFilePath 
                            ? `/v1/files/download?filePath=${encodeURIComponent(formData.photoFilePath)}`
                            : null)
                  }
                  isProcessing={processingLock.photo}
                  processingStatus={processingStatus.photo}
                  onRemove={() => handleFileRemove('photo')}
                  onView={() => {
                    const url = formData.photoPreview || (formData.photoFilePath 
                                  ? `/v1/files/download?filePath=${encodeURIComponent(formData.photoFilePath)}`
                                  : null);
                    if (url) window.open(url, '_blank');
                  }}
                  error={errors.photo}
                  acceptedTypes={['.jpg', '.jpeg', '.png', 'image/*']}
                  description=""
                  required={true}
                  customStyles={{
                    container: { p: 0, border: 'none' },
                    previewBox: { 
                      width: '100%',
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      p: 2
                    },
                    previewImage: {
                      width: { xs: '100%', sm: 140 },
                      height: 140
                    },
                    uploadBox: { 
                      display: 'flex', 
                      justifyContent: 'center', 
                      p: 2, 
                      border: '2px dashed #1976d2', 
                      borderRadius: 2,
                      backgroundColor: '#f5f5f5'
                    },
                    uploadButton: { 
                      variant: 'contained', 
                      color: 'primary', 
                      size: 'medium', 
                      label: 'Upload Photo', 
                      sx: { 
                        textTransform: 'none', 
                        fontWeight: 600,
                        px: 3,
                        '&.Mui-disabled': { 
                          color: 'text.secondary', 
                          borderColor: 'divider', 
                          opacity: 1 
                        } 
                      } 
                    },
                    removeButton: { 
                      variant: 'outlined', 
                      color: 'error', 
                      size: 'small', 
                      sx: { textTransform: 'none' } 
                    }
                  }}
                />
              </Card>
            </Grid>

            {/* Additional Employee Documents Section */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 3, mb: 3, border: '2px solid #1976d2', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Description color="primary" sx={{ mr: 1, fontSize: 28 }} />
                  <Typography variant="h6" color="primary">{isStakeholder ? 'Stakeholder Documents' : 'Employee Documents'}</Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <strong>Important:</strong> Please upload the required documents. These documents are needed for verification and record-keeping.
                </Typography>

                {/* Passport Copy Upload - Multiple Documents */}
                <MultipleDocumentUpload
                  documentType="passportCopy"
                  label="Passport Copy"
                  onFileSelect={(e, docType, index) => handleFileUpload(e, docType, index)}
                  files={formData.passportCopyFiles || []}
                  previewUrls={formData.passportCopyFiles?.map(file => file.preview) || []}
                  processingLock={processingLock}
                  processingStatus={processingStatus}
                  onRemove={(docType, index) => handleFileRemove(docType, index)}
                  onView={(docType, index) => {
                    const files = formData.passportCopyFiles || [];
                    if (files[index]) {
                      // Check if this is a dynamic service field
                      const isDynamicServiceField = isUUID(docType);
                      
                      // Use the new helper function to reliably extract filePath
                      const filePath = getFilePathFromField(files[index]);
                      
                      if (isDynamicServiceField && filePath) {
                        // For dynamic service fields, use file path URL
                        const url = `/v1/files/download?filePath=${encodeURIComponent(filePath)}`;
                        console.log('Opening passportCopy dynamic service field document with URL:', url);
                        window.open(url, '_blank');
                      } else if (files[index].preview) {
                        // For static fields or local previews, use preview URL
                        console.log('Opening passportCopy local file document with preview URL');
                        window.open(files[index].preview, '_blank');
                      } else {
                        console.warn('Unable to view passportCopy document: No file or filePath found');
                        setSnackbar({
                          open: true,
                          message: 'Unable to view passport copy: No file or file path found',
                          severity: 'warning'
                        });
                      }
                    }
                  }}
                  onAddMore={() => handleAddMoreDocuments('passportCopy')}
                  error={errors.passportCopy}
                  acceptedTypes={['.jpg', '.jpeg', '.png', '.pdf', 'image/*', 'application/pdf']}
                  description="Upload a copy of the employee's passport"
                  required={true}
                />

                {/* Visa Page Upload - Multiple Documents */}
                <MultipleDocumentUpload
                  documentType="visaPage"
                  label="Visa Page"
                  onFileSelect={(e, docType, index) => handleFileUpload(e, docType, index)}
                  files={formData.visaPageFiles || []}
                  previewUrls={formData.visaPageFiles?.map(file => file.preview) || []}
                  processingLock={processingLock}
                  processingStatus={processingStatus}
                  onRemove={(docType, index) => handleFileRemove(docType, index)}
                  onView={(docType, index) => {
                    const files = formData.visaPageFiles || [];
                    if (files[index]) {
                      // Check if this is a dynamic service field
                      const isDynamicServiceField = isUUID(docType);
                      
                      // Use the new helper function to reliably extract filePath
                      const filePath = getFilePathFromField(files[index]);
                      
                      if (isDynamicServiceField && filePath) {
                        // For dynamic service fields, use file path URL
                        const url = `/v1/files/download?filePath=${encodeURIComponent(filePath)}`;
                        console.log('Opening visaPage dynamic service field document with URL:', url);
                        window.open(url, '_blank');
                      } else if (files[index].preview) {
                        // For static fields or local previews, use preview URL
                        console.log('Opening visaPage local file document with preview URL');
                        window.open(files[index].preview, '_blank');
                      } else {
                        console.warn('Unable to view visaPage document: No file or filePath found');
                        setSnackbar({
                          open: true,
                          message: 'Unable to view visa page: No file or file path found',
                          severity: 'warning'
                        });
                      }
                    }
                  }}
                />

                {/* Birth Certificate Upload - Multiple Documents */}
                <MultipleDocumentUpload
                  documentType="birthCertificate"
                  label="Birth Certificate"
                  onFileSelect={(e, docType, index) => handleFileUpload(e, docType, index)}
                  files={formData.birthCertificateFiles || []}
                  previewUrls={formData.birthCertificateFiles?.map(file => file.preview) || []}
                  processingLock={processingLock}
                  processingStatus={processingStatus}
                  onRemove={(docType, index) => handleFileRemove(docType, index)}
                  onView={(docType, index) => {
                    const files = formData.birthCertificateFiles || [];
                    if (files[index]) {
                      // Check if this is a dynamic service field
                      const isDynamicServiceField = isUUID(docType);
                      
                      if (isDynamicServiceField) {
                        // For dynamic service fields, use our improved getDocumentUrl function
                        const url = getDocumentUrl(docType, files[index]);
                        if (url) {
                          console.log('Opening birth certificate dynamic service field document with URL:', url);
                          window.open(url, '_blank');
                        } else {
                          console.warn('Unable to view birth certificate: No file path found');
                          setSnackbar({
                            open: true,
                            message: 'Unable to view Birth Certificate: No file path found',
                            severity: 'warning'
                          });
                        }
                      } else if (files[index].file) {
                        // For static fields or local files, create object URL
                        console.log('Opening birth certificate local file document with object URL');
                        window.open(URL.createObjectURL(files[index].file), '_blank');
                      } else if (files[index].preview) {
                        // Use preview URL if available
                        window.open(files[index].preview, '_blank');
                      } else {
                        console.warn('Unable to view birth certificate: No file or file path found');
                        setSnackbar({
                          open: true,
                          message: 'Unable to view Birth Certificate: No file or file path found',
                          severity: 'warning'
                        });
                      }
                    }
                  }}
                  onAddMore={() => handleAddMoreDocuments('birthCertificate')}
                  error={errors.birthCertificate}
                  acceptedTypes={['.jpg', '.jpeg', '.png', '.pdf', 'image/*', 'application/pdf']}
                  description="Upload the employee's birth certificate"
                  required={false}
                />

                {/* Emirates ID Upload - Multiple Documents */}
                <MultipleDocumentUpload
                  documentType="emiratesId"
                  label="Emirates ID"
                  onFileSelect={(e, docType, index) => handleFileUpload(e, docType, index)}
                  files={formData.emiratesIdFiles || []}
                  previewUrls={formData.emiratesIdFiles?.map(file => file.preview) || []}
                  processingLock={processingLock}
                  processingStatus={processingStatus}
                  onRemove={(docType, index) => handleFileRemove(docType, index)}
                  onView={(docType, index) => {
                    const files = formData.emiratesIdFiles || [];
                    if (files[index]) {
                      const f = files[index];
                      if (f.filePath) {
                        const url = `/v1/files/download?filePath=${encodeURIComponent(f.filePath)}`;
                        window.open(url, '_blank');
                      } else if (f.preview) {
                        window.open(f.preview, '_blank');
                      }
                    }
                  }}
                  onAddMore={() => handleAddMoreDocuments('emiratesId')}
                  error={errors.emiratesIdDocument}
                  acceptedTypes={['.jpg', '.jpeg', '.png', '.pdf', 'image/*', 'application/pdf']}
                  description="Upload the employee's Emirates ID"
                  required={false}
                />
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'background.paper', borderLeft: '4px solid #3f51b5' }}>
                <Box display="flex" alignItems="center">
                  <Person color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">Personal Information</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Enter employee personal details and identification information.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="First Name (English)"
                name="firstNameEn"
                value={formData.firstNameEn}
                onChange={handleChange}
                autoComplete="new-given-name"
                fullWidth
                required
                error={!!errors.firstNameEn}
                helperText={errors.firstNameEn}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Middle Name (English)"
                name="middleNameEn"
                value={formData.middleNameEn}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Last Name (English)"
                name="lastNameEn"
                value={formData.lastNameEn}
                onChange={handleChange}
                autoComplete="new-family-name"
                fullWidth
                required
                error={!!errors.lastNameEn}
                helperText={errors.lastNameEn}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Mobile Number"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleChange}
                autoComplete="new-phone"
                fullWidth
                required
                error={!!errors.mobileNumber}
                helperText={errors.mobileNumber}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Date of Birth"
                name="dob"
                type="date"
                value={formData.dob}
                onChange={handleChange}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                error={!!errors.dob}
                helperText={errors.dob}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required error={!!errors.gender}>
                <InputLabel>Gender</InputLabel>
                <Select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  label="Gender"
                >
                  {GENDERS.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
                {errors.gender && <FormHelperText>{errors.gender}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Nationality"
                name="nationality"
                value={formData.nationality || ''}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="UID (Optional)"
                name="uid"
                value={formData.uid || ''}
                onChange={(e) => {
                  // Custom validation for UID
                  const value = e.target.value;
                  handleChange(e);
                  
                  // Clear error if field is empty (since it's optional)
                  if (!value) {
                    if (errors.uid) {
                      const newErrors = {...errors};
                      delete newErrors.uid;
                      setErrors(newErrors);
                    }
                    return;
                  }
                  
                  // Validate format - only allow alphanumeric, hyphens, and underscores
                  if (!/^[a-zA-Z0-9_-]+$/.test(value)) {
                    setErrors({
                      ...errors,
                      uid: 'UID must contain only letters, numbers, hyphens, and underscores'
                    });
                  } else if (value.length > 50) {
                    setErrors({
                      ...errors,
                      uid: 'UID must be 50 characters or less'
                    });
                  } else if (errors.uid) {
                    const newErrors = {...errors};
                    delete newErrors.uid;
                    setErrors(newErrors);
                  }
                }}
                fullWidth
                error={!!errors.uid}
                helperText={errors.uid || "Enter a custom UID or leave blank to auto-generate"}
              />
            </Grid>

            {/* Passport Information Section */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
                <Typography variant="h6" sx={{ color: 'primary.main' }}>
                  Passport Information
                </Typography>
                <DocumentPreviewIcon 
                  docType="passportCopy" 
                  previewUrl={formData.passportCopyPreview} 
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                label="Passport Number"
                name="passportNumber"
                value={formData.passportNumber}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.passportNumber}
                helperText={errors.passportNumber}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Passport Issue Date"
                name="passportIssue"
                type="date"
                value={formData.passportIssue}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.passportIssue}
                helperText={errors.passportIssue}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Passport Expiry Date"
                name="passportExpiry"
                type="date"
                value={formData.passportExpiry}
                onChange={handleChange}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                error={!!errors.passportExpiry}
                helperText={errors.passportExpiry}
              />
            </Grid>
            
            {/* Visa Information Section */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
                <Typography variant="h6" sx={{ color: 'primary.main' }}>
                  Visa Information
                </Typography>
                <DocumentPreviewIcon 
                  docType="visaPage" 
                  previewUrl={formData.visaPagePreview} 
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                label="Visa Number"
                name="visaNumber"
                value={formData.visaNumber || ''}
                onChange={handleChange}
                fullWidth
                error={!!errors.visaNumber}
                helperText={errors.visaNumber}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                label="Visa Type"
                name="visaType"
                value={formData.visaType || ''}
                onChange={handleChange}
                fullWidth
                error={!!errors.visaType}
                helperText={errors.visaType}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Autocomplete
                freeSolo
                options={professionSearchResults}
                loading={professionSearchLoading}
                disabled={isProfessionAutoMatched} // Only disable if auto-matched from visa
                getOptionLabel={(option) => {
                  // Handle both string and object
                  if (typeof option === 'string') return option;
                  return option.title || '';
                }}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  return option.id === value.id;
                }}
                value={formData.professionId && formData.profession ? { id: formData.professionId, title: formData.profession } : null}
                onChange={handleProfessionSelect}
                inputValue={professionInputValue}
                onInputChange={(event, newInputValue, reason) => {
                  // If reason is 'reset', it means user selected an option or cleared
                  if (reason === 'reset') {
                    setProfessionInputValue(newInputValue);
                    return;
                  }
                  
                  setProfessionInputValue(newInputValue);
                  if (!isProfessionAutoMatched) { // Only search if not auto-matched
                    searchProfessions(newInputValue);
                  }
                }}
                onBlur={() => {
                  // If user typed but didn't select (no professionId), clear the input
                  if (!formData.professionId && professionInputValue) {
                    setProfessionInputValue('');
                    setProfessionSearchResults([]);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Profession"
                    placeholder={isProfessionAutoMatched ? "Auto-matched from visa" : "Type at least 2 characters to search"}
                    error={!!professionMatchError || !!errors.profession}
                    helperText={
                      professionMatchError 
                        ? professionMatchError 
                        : isProfessionAutoMatched && formData.profession 
                          ? `✓ Auto-matched: ${formData.profession}` 
                          : professionInputValue && professionInputValue.length < 2
                            ? 'Type at least 2 characters to search'
                            : ''
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {professionSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2">{option.title}</Typography>
                      {option.description && (
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                noOptionsText={
                  professionInputValue && professionInputValue.length < 2
                    ? "Type at least 2 characters"
                    : "No professions found"
                }
              />
            </Grid>   
            
            <Grid item xs={12} sm={4}>
              <TextField
                label="Visa Issue Date"
                name="visaIssueDate"
                type="date"
                value={formData.visaIssueDate || ''}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.visaIssueDate}
                helperText={errors.visaIssueDate}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                label="Visa Expiry Date"
                name="visaExpiryDate"
                type="date"
                value={formData.visaExpiryDate || ''}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.visaExpiryDate}
                helperText={errors.visaExpiryDate}
              />
            </Grid>
            
            {/* Emirates ID Information Section */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
                <Typography variant="h6" sx={{ color: 'primary.main' }}>
                  Emirates ID Information
                </Typography>
                <DocumentPreviewIcon 
                  docType="emiratesId" 
                  previewUrl={formData.emiratesIdPreview} 
                />
              </Box>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Emirates ID"
                name="emiratesId"
                value={formData.emiratesId}
                onChange={handleChange}
                fullWidth
                error={!!errors.emiratesId}
                helperText={errors.emiratesId}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Emirates ID Issue Date"
                name="emiratesIdIssueDate"
                type="date"
                value={formData.emiratesIdIssueDate}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Emirates ID Expiry Date"
                name="emiratesIdExpiryDate"
                type="date"
                value={formData.emiratesIdExpiryDate}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Government Requirements Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 0.5, color: 'primary.main' }}>
                Government Requirements
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.requireOhc}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, requireOhc: e.target.checked }));
                    }}
                    name="requireOhc"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    OHC Required
                  </Typography>
                }
                sx={{ mt: 0.25 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.requireDrivingLicense}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, requireDrivingLicense: e.target.checked }));
                    }}
                    name="requireDrivingLicense"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    Driving License Required
                  </Typography>
                }
                sx={{ mt: 0.25 }}
              />
            </Grid>
          </Grid>
        );
      
      case 1: // Employment Details
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'background.paper', borderLeft: '4px solid #4caf50' }}>
                <Box display="flex" alignItems="center">
                  <Work color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">{isStakeholder ? 'Stakeholder Details' : 'Employment Details'}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {isStakeholder ? 'Enter stakeholder role and upload required documents.' : 'Enter employment information and upload required documents.'}
                </Typography>
              </Paper>
            </Grid>

            {isStakeholder ? (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required error={!!errors.employeeRole}>
                    <InputLabel>Stakeholder Role</InputLabel>
                    <Select
                      name="employeeRole"
                      value={formData.employeeRole}
                      onChange={handleChange}
                      label="Stakeholder Role"
                    >
                      {STAKEHOLDER_ROLES.map(role => (
                        <MenuItem key={role.value} value={role.value}>
                          {role.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.employeeRole && <FormHelperText>{errors.employeeRole}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Ownership Percentage"
                    name="ownershipPercentage"
                    value={formData.ownershipPercentage}
                    onChange={handleChange}
                    fullWidth
                    placeholder="e.g. 25%"
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required error={!!errors.department}>
                    <InputLabel>Department</InputLabel>
                    <Select
                      name="department"
                      value={formData.department ? JSON.stringify(formData.department) : ''}
                      onChange={(e) => {
                        const dept = e.target.value ? JSON.parse(e.target.value) : null;
                        setFormData(prev => ({ ...prev, department: dept }));
                        if (errors.department) {
                          const newErrors = {...errors};
                          delete newErrors.department;
                          setErrors(newErrors);
                        }
                      }}
                      label="Department"
                    >
                      {departments.map(dept => (
                        <MenuItem key={dept.id} value={JSON.stringify(dept)}>
                          {dept.departmentName}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.department && <FormHelperText>{errors.department}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required error={!!errors.position}>
                    <InputLabel>Position</InputLabel>
                    <Select
                      name="position"
                      value={formData.position ? JSON.stringify(formData.position) : ''}
                      onChange={(e) => {
                        const pos = e.target.value ? JSON.parse(e.target.value) : null;
                        setFormData(prev => ({ ...prev, position: pos }));
                        if (errors.position) {
                          const newErrors = {...errors};
                          delete newErrors.position;
                          setErrors(newErrors);
                        }
                      }}
                      label="Position"
                    >
                      {filteredPositions.map(pos => (
                        <MenuItem key={pos.id} value={JSON.stringify(pos)}>
                          {pos.positionName}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.position && <FormHelperText>{errors.position}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Start Date"
                    name="startDate"
                    type="date"
                    value={formData.startDate}
                    onChange={handleChange}
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.startDate}
                    helperText={errors.startDate}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Employee ID"
                    name="employeeId"
                    value={formData.employeeId}
                    onChange={handleChange}
                    fullWidth
                    placeholder="Leave blank to auto-generate"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Reports To"
                    name="reportsTo"
                    value={formData.reportsTo}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Contract Type</InputLabel>
                    <Select
                      name="contractType"
                      value={formData.contractType}
                      onChange={handleChange}
                      label="Contract Type"
                    >
                      <MenuItem value="">Select Type</MenuItem>
                      <MenuItem value="Full-time">Full-time</MenuItem>
                      <MenuItem value="Part-time">Part-time</MenuItem>
                      <MenuItem value="Contract">Contract</MenuItem>
                      <MenuItem value="Temporary">Temporary</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={handleChange('status')}
                  label="Status"
                >
                  {EMPLOYEE_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>
                    <CloudUpload sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Document Upload
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body1" fontWeight="500" gutterBottom>
                        {isStakeholder ? 'Required Documents' : 'Required Employee Documents'}
                      </Typography>
                      <Typography variant="body2" gutterBottom color="text.secondary" mb={2}>
                        Please upload the following required documents. Accepted formats: PDF, JPG, PNG
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <DocumentUploadField
                        documentType="passportCopy"
                        label="Passport Copy"
                        onFileSelect={(e) => handleFileUpload(e, 'passportCopy')}
                        file={formData.passportCopyFile}
                        previewUrl={formData.passportCopyPreview}
                        isProcessing={processingLock.passportCopy}
                        processingStatus={processingStatus.passportCopy}
                        onRemove={() => handleFileRemove('passportCopy')}
                        onView={() => {
                          // Passport copy is a static field, so we can use the preview URL directly
                          if (formData.passportCopyPreview) {
                            window.open(formData.passportCopyPreview, '_blank');
                          }
                        }}
                        error={errors.passportCopy}
                        acceptedTypes={['.pdf', '.jpg', '.jpeg', '.png']}
                        required={false}
                        customStyles={{
                          container: { p: 2, mb: 2 }
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <DocumentUploadField
                        documentType="visaPage"
                        label="Visa Page"
                        onFileSelect={(e) => handleFileUpload(e, 'visaPage')}
                        file={formData.visaPageFile}
                        previewUrl={formData.visaPagePreview}
                        isProcessing={processingLock.visaPage}
                        processingStatus={processingStatus.visaPage}
                        onRemove={() => handleFileRemove('visaPage')}
                        onView={() => {
                          // Visa page is a static field, so we can use the preview URL directly
                          if (formData.visaPagePreview) {
                            window.open(formData.visaPagePreview, '_blank');
                          }
                        }}
                        error={errors.visaPage}
                        acceptedTypes={['.pdf', '.jpg', '.jpeg', '.png']}
                        required={false}
                        customStyles={{
                          container: { p: 2, mb: 2 }
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <DocumentUploadField
                        documentType="labor_contract"
                        label="Labour Contract"
                        onFileSelect={(e) => handleFileUpload(e, 'labor_contract')}
                        file={formData.documents.find(doc => doc.type === 'labor_contract')?.file}
                        previewUrl={formData.documents.find(doc => doc.type === 'labor_contract')?.file ? 
                          URL.createObjectURL(formData.documents.find(doc => doc.type === 'labor_contract').file) : null}
                        isProcessing={processingLock.labor_contract}
                        processingStatus={processingStatus.labor_contract}
                        onRemove={() => {
                          const index = formData.documents.findIndex(doc => doc.type === 'labor_contract');
                          if (index !== -1) removeDocument(index);
                        }}
                        onView={() => {
                          const doc = formData.documents.find(doc => doc.type === 'labor_contract');
                          if (doc) {
                            // Check if this is a dynamic service field
                            const isDynamicServiceField = isUUID(doc.type);
                            
                            // Use the new helper function to reliably extract filePath
                            const filePath = getFilePathFromField(doc);
                            
                            if (isDynamicServiceField && filePath) {
                              // For dynamic service fields, use file path URL
                              const url = `/v1/files/download?filePath=${encodeURIComponent(filePath)}`;
                              console.log('Opening dynamic service field document with URL:', url);
                              window.open(url, '_blank');
                            } else if (doc.file) {
                              // For static fields or local files, create object URL
                              console.log('Opening local file document with object URL');
                              window.open(URL.createObjectURL(doc.file), '_blank');
                            } else {
                              console.warn('Unable to view document: No file or filePath found');
                              setSnackbar({
                                open: true,
                                message: 'Unable to view document: No file or file path found',
                                severity: 'warning'
                              });
                            }
                          }
                        }}
                        error={errors.labor_contract}
                        acceptedTypes={['.pdf', '.jpg', '.jpeg', '.png']}
                        required={false}
                        customStyles={{
                          container: { p: 2, mb: 2 }
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <DocumentUploadField
                        documentType="salaryCertificate"
                        label="Salary Certificate"
                        onFileSelect={(e) => handleFileUpload(e, 'salaryCertificate')}
                        file={formData.salaryCertificateFile}
                        previewUrl={formData.salaryCertificatePreview}
                        isProcessing={processingLock.salaryCertificate}
                        processingStatus={processingStatus.salaryCertificate}
                        onRemove={() => handleFileRemove('salaryCertificate')}
                        onView={() => {
                          // Salary certificate is a static field, so we can use the preview URL directly
                          if (formData.salaryCertificatePreview) {
                            window.open(formData.salaryCertificatePreview, '_blank');
                          }
                        }}
                        error={errors.salaryCertificate}
                        acceptedTypes={['.pdf', '.jpg', '.jpeg', '.png']}
                        required={false}
                        customStyles={{
                          container: { p: 2, mb: 2 }
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <DocumentUploadField
                        documentType="other_document"
                        label="Other Documents"
                        onFileSelect={(e) => handleFileUpload(e, 'other_document')}
                        file={formData.documents.find(doc => doc.type === 'other_document')?.file}
                        previewUrl={formData.documents.find(doc => doc.type === 'other_document')?.file ? 
                          URL.createObjectURL(formData.documents.find(doc => doc.type === 'other_document').file) : null}
                        isProcessing={processingLock.other_document}
                        processingStatus={processingStatus.other_document}
                        onRemove={() => {
                          const index = formData.documents.findIndex(doc => doc.type === 'other_document');
                          if (index !== -1) removeDocument(index);
                        }}
                        onView={() => {
                          const doc = formData.documents.find(doc => doc.type === 'other_document');
                          if (doc) {
                            // Check if this is a dynamic service field
                            const isDynamicServiceField = isUUID(doc.type);
                            
                            // Use the new helper function to reliably extract filePath
                            const filePath = getFilePathFromField(doc);
                            
                            if (isDynamicServiceField && filePath) {
                              // For dynamic service fields, use file path URL
                              const url = `/v1/files/download?filePath=${encodeURIComponent(filePath)}`;
                              console.log('Opening dynamic service field document with URL:', url);
                              window.open(url, '_blank');
                            } else if (doc.file) {
                              // For static fields or local files, create object URL
                              console.log('Opening local file document with object URL');
                              window.open(URL.createObjectURL(doc.file), '_blank');
                            } else {
                              console.warn('Unable to view document: No file or filePath found');
                              setSnackbar({
                                open: true,
                                message: 'Unable to view document: No file or file path found',
                                severity: 'warning'
                              });
                            }
                          }
                        }}
                        error={errors.other_document}
                        acceptedTypes={['.pdf', '.jpg', '.jpeg', '.png']}
                        required={false}
                        customStyles={{
                          container: { p: 2, mb: 2 }
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      {formData.documents.length > 0 ? (
                        <>
                          <Typography variant="subtitle2" gutterBottom sx={{color: 'text.primary', fontWeight: 500}}>
                            Uploaded Documents ({formData.documents.length})
                          </Typography>
                          {formData.documents.map((doc, index) => (
                            <Card key={index} variant="outlined" sx={{ p: 1, mb: 1, display: 'flex', alignItems: 'center' }}>
                              <Description sx={{ mr: 1 }} />
                              <Typography variant="body2" sx={{ flex: 1, color: 'text.primary' }}>
                                {doc.name}
                              </Typography>
                              <IconButton size="small" onClick={() => removeDocument(index)}>
                                <Delete />
                              </IconButton>
                            </Card>
                          ))}
                        </>
                      ) : (
                        <Typography variant="subtitle2" sx={{color: 'text.primary', fontWeight: 500}}>
                          No documents uploaded
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        );
      
      case 2: // Contact & Access Information
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'background.paper', borderLeft: '4px solid #ff9800' }}>
                <Box display="flex" alignItems="center">
                  <VpnKey color="primary" sx={{ mr: 1 }} />
                  <Typography variant="subtitle1">Contact & Access Information</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Enter contact information and optionally create system access credentials.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="PO Box"
                name="poBox"
                value={formData.poBox}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="subtitle1">
                  System Access Credentials
                </Typography>
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                    Requires Login Access
                  </Typography>
                  <Switch
                    checked={formData.requiresLogin}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        requiresLogin: e.target.checked
                      }));
                      
                      // Clear any login-related errors if disabling login
                      if (!e.target.checked) {
                        const newErrors = {...errors};
                        delete newErrors.username;
                        delete newErrors.password;
                        delete newErrors.confirmPassword;
                        setErrors(newErrors);
                      }
                    }}
                    color="primary"
                  />
                </Box>
              </Box>
              {!formData.requiresLogin && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This employee will be created without login access. You can activate login access later.
                </Alert>
              )}
            </Grid>
            
            {formData.requiresLogin && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Username"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    fullWidth
                    required={formData.requiresLogin}
                    error={!!errors.username}
                    helperText={errors.username || 'Employee will use this to log in'}
                    sx={{ '& .MuiInputBase-input': { color: 'text.primary' } }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="access-level-label">Access Level</InputLabel>
                    <Select
                      labelId="access-level-label"
                      name="accessLevel"
                      label="Access Level"
                      value={formData.accessLevel}
                      onChange={handleChange('accessLevel')}
                      disabled // In this phase, we only allow CUSTOMER_EMPLOYEE
                      sx={{ '& .MuiSelect-select': { color: 'text.primary' } }}
                    >
                      <MenuItem value="CUSTOMER_EMPLOYEE">Employee (Standard Access)</MenuItem>
                      <MenuItem value="CUSTOMER_ADMIN">Admin (Advanced Access)</MenuItem>
                    </Select>
                    <FormHelperText sx={{ color: 'text.secondary' }}>
                      Standard level grants basic access to employee self-service features
                    </FormHelperText>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange('password')}
                    error={!!errors.password}
                    helperText={errors.password || 'At least 8 characters'}
                    required={formData.requiresLogin}
                    sx={{ '& .MuiInputBase-input': { color: 'text.primary' } }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Confirm Password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange('confirmPassword')}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword || ''}
                    required={formData.requiresLogin}
                    sx={{ '& .MuiInputBase-input': { color: 'text.primary' } }}
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12}>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                Review Information
              </Typography>
              <Typography variant="body2" paragraph>
                Please review the information before submitting.
              </Typography>
              
              <Paper sx={{ p: 3, backgroundColor: 'background.paper' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">Personal Information</Typography>
                    <Typography variant="body2">
                      <strong>Name:</strong>{' '}
                      {[formData.firstNameEn || formData.firstNameAr,
                        formData.middleNameEn || formData.middleNameAr,
                        formData.lastNameEn || formData.lastNameAr]
                        .filter(Boolean)
                        .join(' ') || '-'}
                      <br />
                      <strong>Email:</strong> {formData.email}<br />
                      <strong>Phone:</strong> {formData.mobileNumber}<br />
                      {formData.nationalId && <><strong>National ID:</strong> {formData.nationalId}<br /></>}
                      {formData.passportNumber && <><strong>Passport:</strong> {formData.passportNumber}<br /></>}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    {isStakeholder ? (
                      <>
                        <Typography variant="subtitle2">Stakeholder Details</Typography>
                        <Typography variant="body2">
                          <strong>Role:</strong> {formData.employeeRole || 'Not specified'}<br />
                          <strong>Ownership %:</strong> {formData.ownershipPercentage || 'Not specified'}<br />
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="subtitle2">Job Details</Typography>
                        <Typography variant="body2">
                          <strong>Department:</strong> {formData.department?.departmentName || 'Not specified'}<br />
                          <strong>Position:</strong> {formData.position?.positionName || 'Not specified'}<br />
                          <strong>Start Date:</strong> {formData.startDate || 'Not specified'}<br />
                          <strong>Contract Type:</strong> {formData.contractType}<br />
                        </Typography>
                      </>
                    )}
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Documents</Typography>
                    <Typography variant="body2">
                      {formData.documents.length > 0 
                        ? `${formData.documents.length} document(s) uploaded` 
                        : 'No documents uploaded'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        );
      
      default:
        return 'Unknown step';
    }
  };

  // Conflict Resolution Dialog
  const ConflictResolutionDialog = () => {
    // Group conflicts by field for better organization
    const conflictsByField = conflicts.reduce((acc, conflict) => {
      if (!acc[conflict.field]) {
        acc[conflict.field] = [];
      }
      acc[conflict.field].push(conflict);
      return acc;
    }, {});
    
    // Count resolved and total conflicts for progress indicator
    const totalFields = Object.keys(conflictsByField).length;
    const resolvedFields = Object.values(conflictsByField)
      .filter(fieldConflicts => fieldConflicts.length === 0).length;
    const progress = totalFields > 0 ? (resolvedFields / totalFields) * 100 : 0;
    
    return (
      <Dialog
        open={showConflictDialog}
        onClose={() => setShowConflictDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <WarningIcon sx={{ mr: 1 }} />
              Cross-Document Data Conflicts
            </Box>
            <Typography variant="body2">
              {resolvedFields} of {totalFields} resolved
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          {/* Progress bar for conflict resolution */}
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ mb: 2, height: 8, borderRadius: 4 }} 
          />
          
          <Typography variant="body1" paragraph>
            The system detected conflicts between data extracted from different documents. 
            Please select which value to use for each field:
          </Typography>
          
          {conflicts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              All conflicts have been resolved.
            </Typography>
          ) : (
            <List>
              {conflicts.map((conflict, index) => (
                <ListItem 
                  key={index} 
                  divider={index < conflicts.length - 1}
                  sx={{
                    bgcolor: index % 2 === 0 ? 'background.paper' : 'action.hover',
                    borderRadius: 1,
                    mb: 1,
                    py: 1 // Reduce vertical padding
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {conflict.label}
                      </Typography>
                      <Chip 
                        size="small" 
                        label={conflict.source} 
                        color="info" 
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={9}>
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Button 
                          variant="outlined" 
                          color="primary"
                          onClick={() => handleResolveConflict(conflict.field, conflict.currentValue)}
                          startIcon={<CheckCircle />}
                          fullWidth
                          sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                        >
                          <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                            <Box>
                              <Typography variant="body2" fontWeight="bold" color="text.primary">
                                {conflict.currentSource ? `From ${conflict.currentSource}:` : 'Current value:'}
                              </Typography>
                              <Typography color="text.primary" sx={{ fontWeight: 500 }}>{conflict.currentValue}</Typography>
                            </Box>
                            <Tooltip title="Use this value">
                              <IconButton size="small" color="primary">
                                <CheckCircleOutlineIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Button>
                        
                        <Button 
                          variant="contained" 
                          color="secondary"
                          onClick={() => handleResolveConflict(conflict.field, conflict.newValue)}
                          startIcon={<CheckCircle />}
                          fullWidth
                          sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                        >
                          <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                            <Box>
                              <Typography variant="body2" fontWeight="bold" sx={{ color: 'common.white' }}>
                                {conflict.newSource ? `From ${conflict.newSource}:` : 'Extracted value:'}
                              </Typography>
                              <Typography sx={{ color: 'common.white', fontWeight: 500 }}>{conflict.newValue}</Typography>
                            </Box>
                            <Tooltip title="Use this value">
                              <IconButton size="small" sx={{ color: 'common.white' }}>
                                <CheckCircleOutlineIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Button>
                        
                        {/* Document priority indicator */}
                        {conflict.priority !== undefined && (
                          <Typography variant="caption" color="text.secondary" align="right">
                            {conflict.priority === 0 ? 
                              <Chip size="small" label="Highest Priority Source" color="success" variant="outlined" /> : 
                              `Priority level: ${conflict.priority + 1}`
                            }
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={() => {
              // Clear all conflicts and close dialog
              setConflicts([]);
              setShowConflictDialog(false);
              setSnackbar({
                open: true,
                message: 'All conflicts have been dismissed',
                severity: 'info'
              });
            }} 
            color="error"
          >
            Dismiss All
          </Button>
          <Button onClick={() => setShowConflictDialog(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };
  
  // Photo Auto-Fix Confirmation Dialog
  const PhotoAutoFixDialog = () => {
    return (
      <Dialog
        open={photoAutoFixDialog.open}
        onClose={handleCloseAutoFixDialog}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown // Prevent closing with Escape key
      >
        <DialogTitle sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
          <Box display="flex" alignItems="center">
            <WarningIcon sx={{ mr: 1 }} />
            <Typography variant="subtitle1" fontWeight="500" sx={{ color: 'inherit' }}>
              Photo Validation Issues Detected
            </Typography>
            {photoAutoFixDialog.issues.some(issue => issue.includes('timed out')) && (
              <Chip 
                label="Timed Out" 
                color="error" 
                size="small" 
                sx={{ ml: 2 }} 
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" paragraph>
            {photoAutoFixDialog.issues.some(issue => issue.includes('timed out')) 
              ? 'The backend validation timed out after 1 minute. We recommend using our AI auto-fix feature to ensure your photo meets UAE passport/immigration requirements.'  
              : 'The photo you uploaded does not meet UAE passport/immigration requirements. The following issues were detected:'}
          </Typography>
          
          <List>
            {photoAutoFixDialog.issues.map((issue, index) => (
              <ListItem key={index} divider={index < photoAutoFixDialog.issues.length - 1}>
                <ListItemText
                  primary={issue}
                  secondary={
                    photoAutoFixDialog.details[issue.toLowerCase().replace(/ /g, '')] 
                      ? `Required: ${photoAutoFixDialog.details[issue.toLowerCase().replace(/ /g, '')].required}` 
                      : issue.includes('timed out') && photoAutoFixDialog.details.timeout 
                        ? `Required: ${photoAutoFixDialog.details.timeout.required}` 
                        : ''
                  }
                />
              </ListItem>
            ))}
          </List>
          
          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" fontWeight="500" gutterBottom>
                <AutoFixHigh color="secondary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                Fix Photo Using AI
              </Typography>
              <Typography variant="body2" paragraph>
                {photoAutoFixDialog.issues.some(issue => issue.includes('timed out')) 
                  ? 'Our AI can process your photo to meet UAE requirements without waiting for validation to complete. The result will be shown for your approval.' 
                  : 'Our system can automatically fix the detected issues using AI. The result will be shown for your approval.'}
              </Typography>
              <Button 
                onClick={() => handleApplyPhotoAutoFix(photoAutoFixDialog.originalFile)} 
                variant="contained" 
                color="secondary"
                startIcon={<AutoFixHigh />}
                sx={{ 
                  color: '#fff', 
                  fontWeight: 'bold', 
                  py: 0.75, 
                  width: { xs: '90%', sm: '60%' }, 
                  mx: 'auto', 
                  display: 'block',
                  fontSize: '0.875rem'
                }}
              >
                Apply AI Auto-Fix
              </Button>
            </Paper>
            
            <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" fontWeight="500" gutterBottom>
                <PhotoCamera color="primary" sx={{ mr: 1, verticalAlign: 'middle' }} />
                Replace Photo
              </Typography>
              <Typography variant="body2" paragraph>
                Upload a new photo that meets UAE passport/immigration requirements.
              </Typography>
              <Button 
                onClick={handleReplacePhoto}
                variant="contained" 
                color="primary"
                startIcon={<PhotoCamera />}
                sx={{ 
                  fontWeight: 'bold', 
                  py: 0.75, 
                  width: { xs: '90%', sm: '60%' }, 
                  mx: 'auto', 
                  display: 'block',
                  fontSize: '0.875rem'
                }}
              >
                Upload New Photo
              </Button>
            </Paper>
            
            <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" fontWeight="500" gutterBottom>
                <CheckCircle color="success" sx={{ mr: 1, verticalAlign: 'middle' }} />
                Continue with Current Photo
              </Typography>
              <Typography variant="body2" paragraph>
                Proceed with the current photo despite the validation issues.
              </Typography>
              <Button 
                onClick={() => handleContinueWithCurrentPhoto(photoAutoFixDialog.originalFile)}
                variant="contained" 
                color="success"
                startIcon={<CheckCircle />}
                sx={{ 
                  fontWeight: 'bold', 
                  py: 0.75, 
                  width: { xs: '90%', sm: '60%' }, 
                  mx: 'auto', 
                  display: 'block',
                  fontSize: '0.875rem'
                }}
              >
                Continue Anyway
              </Button>
            </Paper>
          </Box>
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            <strong>Disclaimer:</strong> The auto-fix feature uses AI to adjust your photo. While it aims to meet UAE requirements, 
            the result may not be perfect and could still be rejected by authorities. Please review the fixed photo carefully.
          </Typography>
        </DialogContent>
        <DialogActions>
          {/* No cancel button to force user to make a choice from the three options above */}
        </DialogActions>
      </Dialog>
    );
  };
  
  // Extracted Data Summary Dialog
  const ExtractedDataSummaryDialog = () => {
    // Combine all extracted data
    const allExtractedData = {};
    Object.entries(extractedData).forEach(([docType, data]) => {
      Object.entries(data).forEach(([field, value]) => {
        if (!allExtractedData[field]) {
          allExtractedData[field] = { value, sources: [docType] };
        } else {
          allExtractedData[field].sources.push(docType);
        }
      });
    });
    
    // Group fields by category
    const fieldCategories = {
      personal: ['firstName', 'middleName', 'lastName', 'dateOfBirth', 'gender', 'nationality'],
      passport: ['passportNumber', 'passportIssueDate', 'passportExpiryDate'],
      visa: ['visaNumber', 'visaIssueDate', 'visaExpiryDate', 'visaType'],
      employment: ['position', 'employmentStartDate', 'salary'],
      other: []
    };
    
    // Categorize all fields
    const categorizedFields = {};
    Object.entries(allExtractedData).forEach(([field, data]) => {
      let assigned = false;
      Object.entries(fieldCategories).forEach(([category, fields]) => {
        if (fields.includes(field)) {
          if (!categorizedFields[category]) categorizedFields[category] = {};
          categorizedFields[category][field] = data;
          assigned = true;
        }
      });
      
      if (!assigned) {
        if (!categorizedFields.other) categorizedFields.other = {};
        categorizedFields.other[field] = data;
      }
    });
    
    // Function to apply all extracted data
    const applyAllExtractedData = () => {
      setFormData(prev => {
        const updatedData = { ...prev };
        
        Object.entries(allExtractedData).forEach(([field, data]) => {
          // Map extracted field names to form field names
          const formField = field;
          if (data.value && data.value.trim() !== '') {
            updatedData[formField] = data.value;
          }
        });
        
        return updatedData;
      });
      
      setShowExtractedDataDialog(false);
      
      setSnackbar({
        open: true,
        message: 'All extracted data applied to form',
        severity: 'success'
      });
    };
    
    // Helper function to format field labels
    const formatFieldLabel = (field) => {
      return field
        .replace(/([A-Z])/g, ' $1') // Insert space before capital letters
        .replace(/^./, str => str.toUpperCase()); // Capitalize first letter
    };
    
    return (
      <Dialog
        open={showExtractedDataDialog}
        onClose={() => setShowExtractedDataDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
          <Box display="flex" alignItems="center">
            <DataObjectIcon sx={{ mr: 1 }} />
            Extracted Data Summary
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" paragraph>
            The following data was extracted from your uploaded documents:
          </Typography>
          
          {Object.keys(categorizedFields).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No data has been extracted yet. Upload documents to extract information.
            </Typography>
          ) : (
            <>
              {Object.entries(categorizedFields).map(([category, fields]) => (
                <Box key={category} sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ textTransform: 'capitalize', mb: 1 }}>
                    {category} Information
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell>Field</TableCell>
                          <TableCell>Value</TableCell>
                          <TableCell>Source Documents</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(fields).map(([field, data]) => (
                          <TableRow key={field}>
                            <TableCell>
                              <Typography variant="body2">
                                {formatFieldLabel(field)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography 
                                variant="body2"
                                sx={{
                                  fontWeight: formData[field] === data.value ? 'bold' : 'normal',
                                  color: formData[field] === data.value ? 'success.main' : 'text.primary'
                                }}
                              >
                                {data.value}
                                {formData[field] === data.value && (
                                  <Tooltip title="This value is currently used in the form">
                                    <CheckCircle fontSize="small" color="success" sx={{ ml: 1, verticalAlign: 'middle' }} />
                                  </Tooltip>
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box display="flex" gap={0.5} flexWrap="wrap">
                                {data.sources.map((source, idx) => (
                                  <Chip 
                                    key={idx} 
                                    label={source} 
                                    size="small" 
                                    color="primary" 
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExtractedDataDialog(false)} color="primary">
            Close
          </Button>
          {Object.keys(allExtractedData).length > 0 && (
            <Button 
              onClick={applyAllExtractedData} 
              color="primary" 
              variant="contained"
              startIcon={<SaveIcon />}
            >
              Apply All Data
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  };
  
  // Determine if a passport copy is UPLOADED (require filePath), supporting single or multiple inputs
  const hasPassportSelected = (() => {
    const arr = Array.isArray(formData.passportCopyFiles) ? formData.passportCopyFiles : [];
    const anyArrayWithPath = arr.some(d => d && typeof d.filePath === 'string' && d.filePath.length > 0);
    const legacyWithPath = typeof formData.passportCopyFilePath === 'string' && formData.passportCopyFilePath.length > 0;
    return anyArrayWithPath || legacyWithPath;
  })();
  // Debug: trace gating condition (uploaded status)
  console.log('hasPassportSelected (requires filePath):', hasPassportSelected, {
    passportCopyFilesLen: Array.isArray(formData.passportCopyFiles) ? formData.passportCopyFiles.length : 0,
    anyArrayWithPath: Array.isArray(formData.passportCopyFiles) && formData.passportCopyFiles.some(d => d && d.filePath),
    legacyPassportFilePath: formData.passportCopyFilePath || null
  });

  // Determine if an Emirates ID is UPLOADED (require filePath), supporting single or multiple inputs
  const hasEmiratesIdSelected = (() => {
    const arr = Array.isArray(formData.emiratesIdFiles) ? formData.emiratesIdFiles : [];
    const anyArrayWithPath = arr.some(d => d && typeof d.filePath === 'string' && d.filePath.length > 0);
    const legacyWithPath = typeof formData.emiratesIdFilePath === 'string' && formData.emiratesIdFilePath.length > 0;
    return anyArrayWithPath || legacyWithPath;
  })();
  console.log('hasEmiratesIdSelected (requires filePath):', hasEmiratesIdSelected, {
    emiratesIdFilesLen: Array.isArray(formData.emiratesIdFiles) ? formData.emiratesIdFiles.length : 0,
    anyArrayWithPath: Array.isArray(formData.emiratesIdFiles) && formData.emiratesIdFiles.some(d => d && d.filePath),
    legacyEmiratesIdFilePath: formData.emiratesIdFilePath || null
  });

  // Document Preview Icon Component
  const DocumentPreviewIcon = ({ docType, previewUrl }) => {
    if (!previewUrl) return null;

    const handleMouseEnter = (event) => {
      setHoverPreview({
        anchorEl: event.currentTarget,
        imageUrl: previewUrl,
        docType
      });
    };

    const handleMouseLeave = () => {
      setHoverPreview({
        anchorEl: null,
        imageUrl: null,
        docType: null
      });
    };

    return (
      <Box
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{ display: 'inline-flex', ml: 1 }}
      >
        <Tooltip title="Hover to preview document">
          <IconButton
            size="small"
            sx={{ 
              color: 'primary.main',
              '&:hover': { bgcolor: 'primary.light', color: 'white' }
            }}
          >
            <ImageIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h1">
          <IconButton
            edge="start"
            onClick={handleCancel}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          {isStakeholder ? 'Add New Stakeholder' : 'Add New Employee'}
        </Typography>
      </Box>
      
      <Paper elevation={2} sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel 
                icon={
                  index === 0 ? <Person color={activeStep === 0 ? "primary" : "disabled"} /> : 
                  index === 1 ? <Work color={activeStep === 1 ? "primary" : "disabled"} /> : 
                  <VpnKey color={activeStep === 2 ? "primary" : "disabled"} />
                }
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Box mb={4}>
          {getStepContent(activeStep)}
        </Box>
        <Box display="flex" justifyContent="space-between" mt={3}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            startIcon={<ArrowBack />}
            sx={{ color: 'text.primary', borderColor: 'grey.400' }}
          >
            Cancel
          </Button>
          
          <Box>
            {activeStep > 0 && (
              <Button 
                onClick={handleBack}
                sx={{ mr: 1, color: 'text.primary', borderColor: 'grey.400' }}
                startIcon={<KeyboardArrowLeft />}
                variant="outlined"
                disabled={loading}
              >
                Back
              </Button>
            )}

            {activeStep < steps.length - 1 ? (
              <>
                <Button 
                  variant="contained" 
                  onClick={handleNext}
                  endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <KeyboardArrowRight />}
                  color="primary"
                  sx={{ color: 'white' }}
                  disabled={loading}
                  title=""
                >
                  {loading ? 'Validating...' : 'Next'}
                </Button>
              </>
            ) : (
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleSubmit}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                sx={{ color: 'white' }}
              >
                {loading ? 'Saving...' : (isStakeholder ? 'Save Stakeholder' : 'Save Employee')}
              </Button>
            )}
            </Box>
        </Box>
      </Paper>
      
      {/* Render dialogs */}
      <ConflictResolutionDialog />
      <ExtractedDataSummaryDialog />
      <PhotoAutoFixDialog />
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Hover Preview Popover */}
      <Popover
        open={Boolean(hoverPreview.anchorEl)}
        anchorEl={hoverPreview.anchorEl}
        onClose={() => setHoverPreview({ anchorEl: null, imageUrl: null, docType: null })}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        disableRestoreFocus
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 300 }}
        PaperProps={{
          sx: {
            pointerEvents: 'none',
          }
        }}
      >
        <Box
          sx={{
            p: 1,
            maxWidth: 400,
            maxHeight: 500,
            bgcolor: 'background.paper',
            boxShadow: 3,
            borderRadius: 1
          }}
        >
          {hoverPreview.imageUrl && (
            <img
              src={hoverPreview.imageUrl}
              alt={`${hoverPreview.docType} preview`}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                borderRadius: '4px'
              }}
            />
          )}
        </Box>
      </Popover>
    </Container>
  );
}

export default EmployeeCreate;
